import ZoomVideo, {
	Participant as ZoomParticipant,
	ParticipantPropertiesPayload,
	ReconnectReason,
	VideoClient,
	MutedSource,
	AudioChangeAction,
	LeaveAudioSource,
	ExecutedResult,
	ExecutedFailure,
	LiveStreamStatus,
	CaptureVideoOption,
	AudioOption,
	VideoQuality,
	ConnectionState,
	VideoPlayer,
	ActiveMediaFailedCode,
	ScreenShareOption
} from '@zoom/videosdk'
import { computed, ref, watch } from 'vue'
import { jwtDecode } from "jwt-decode";
import Meeting from './meeting'
import { CommandPayload, CommandType, SetParticipantOnCanvasModePayload, setUserCameraCountPayload } from '../types/CommandPayload'
import { ParticipantElement } from '../types/ParticipantElement'
import ElementAddedEventArgs from '../types/ElementAddedEventArgs'
import ElementRemovedEventArgs from '../types/ElementRemovedEventArgs'
import { v4 as uuidv4 } from 'uuid'
import { MeetingEvent } from '../types/MeetingEvent'
import { MeetingParticipant } from '../types/MeetingParticipant'
import { MediaDevice } from '../types/MediaDevice'
import useMeetingConfiguration from '../composables/useMeetingConfiguration';
import { UserMedia } from '../helpers/userMedia';
import { mirrorImage } from '../helpers/image';

enum MeetingConnectionState {
	None = -1,
	Closed = 0,
	Reconnecting = 1,
	Connected = 2,
	Fail = 4,
	Connecting = 5,
	Initializing = 6,
	Full = 7,
}

enum MediaState {
	None = 0,
	AccessingDevice = 1,
	PermissionDenied = 2,
	Accessed = 3,
	Failed = 4,
	Warning = 5,
}
enum MeetingClosedReason {
	Unknown = 0,
	EndedByHost = 1,
	KickedByHost = 2,
	ExpelledByHost = 3
}

enum MeetingReconnectReason {
	Failover = 0,
	JoinSubsession = 1,
	MoveToSubsession = 2,
	BackToMainSession = 3
}


/*

This is a wrapper around the Zoom sdk. There are things that DO belong here, and things that DO NOT belong here.

1. NO BUSINESS LOGIC. That means things like requiring a camera if the user is a host does NOT go here, because
that is busiess logic. Instead, it should be in the component that is using this class, and should set "isCameraRequired"
to true or false based on the user type.

2. NO UI LOGIC. That means things like showing a loading spinner, or showing an error message, do NOT go here. The only
small exception is the creation of the video/avatar elements, which are created here, but the logic for how to
render them belongs in the component that is using this class.


3. NO API LOGIC. That means things like making API calls to get the meeting settings, or getting the user info, should likely NOT
go here. If the settings object or api client is directly referenced, that is a problem.

There are also things that are an EXCEPTION and go here. In the case of Zoom, we don't use their recording, we use a weird AWS hybrid that
works faster than their recorder. So the logic for starting/stopping the recording is in this class. If we were to get fancy in the future, we could
break that component out into its own class, but for now, it is here.

*/
class ZoomMeeting extends Meeting {
	
	
	isUserVideoOn(id: string) {
		if (!this.client) {
			return false
		}
		let isVideoOn = false
		this.client.getAllUser().forEach((user) => {
			if ('' + user.userId == id) {
				this.consoleLog('isUserVideoOn', user.userId, user.bVideoOn)
				isVideoOn = user.bVideoOn
			}
		})
		return isVideoOn
	}

	getName(): string {
		if (!this.client) {
			return ''
		}
		try {
			return this.client?.getCurrentUserInfo().displayName
		} catch {
			return ''
		}
	}
	getUserId(): string {
		if (!this.client) {
			return ''
		}
		try {
			return '' + this.client?.getCurrentUserInfo().userId
		} catch {
			return ''
		}
	}

	getUserIdNumber(): number {
		if (!this.client) {
			return 0
		}
		try {
			return this.client?.getCurrentUserInfo().userId
		} catch {
			return 0
		}
	}
	getMe(): MeetingParticipant | undefined {
		if (!this.client) {
			return undefined
		}
		return this.participants.find((p) => p?.userId === this.getUserId()) as MeetingParticipant;
	}
	getRoomId(): string {
		if (!this.client) {
			return ''
		}
		return this.client?.getSessionInfo().topic
	}
	getSessionId(): string {
		if (!this.client) {
			return ''
		}
		return this.client?.getSessionInfo().sessionId
	}

	getFirstActiveNonHostOrManagerParticipantId() {
		let id = ''
		this.client?.getAllUser().forEach((user) => {
			if (!user.isHost && !user.isManager && user.isInFailover !== true) {
				if (user.bVideoOn) {
					id = '' + user.userId
				}
			}
		})
		return id
	}

	private _preferredCameraId = ref('')
	public get preferredCameraId() {
		return this._preferredCameraId.value
	}
	public set preferredCameraId(value: string) {
		this._preferredCameraId.value = value
	}

	private _preferredMicrophoneId = ref('')
	public get preferredMicrophoneId() {
		return this._preferredMicrophoneId.value
	}
	public set preferredMicrophoneId(value: string) {
		this._preferredMicrophoneId.value = value
	}

	private _preferredSpeakerId = ref('')
	public get preferredSpeakerId() {
		return this._preferredSpeakerId.value
	}
	public set preferredSpeakerId(value: string) {
		this._preferredSpeakerId.value = value
	}

	private _iAmInFailover = ref(false as boolean)
	public get iAmInFailover(): boolean {
		return this._iAmInFailover.value
	}

	private client: typeof VideoClient | null = null
	private get stream(){
		if (!this.client) {
			return null
		}
		return this.client.getMediaStream()
	} 

	private _closedReason = ref(MeetingClosedReason.Unknown)
	public get closedReason() {
		return this._closedReason.value
	}
	
	private _reconnectReason = ref(MeetingReconnectReason.Failover)
	public get reconnectReason() {
		return this._reconnectReason.value
	}

	private _failedReason = ref('')
	public get failedReason() {
		return this._failedReason.value
	}

	private initStarted = ref(false)
	private _isInitialized = ref(false)
	public override isInitialized() {
		return this._isInitialized.value
	}

	private _initializedAt = ref(undefined as number | undefined)
	public get initializedAt() {
		return this._initializedAt.value
	}

	private _virtualBackgroundUrl = ref('')
	public set virtualBackgroundUrl(value: string) {
		this._virtualBackgroundUrl.value = value
	}
	public get virtualBackgroundUrl() {
		return this._virtualBackgroundUrl.value
	}

	private _isMobileDevice = ref(false)
	private _isDesktopLikeMobileDevice = ref(false)
	public get isMobileDevice() {
		return this._isMobileDevice.value
	}
	public get isDesktopDevice() {
		return !this._isMobileDevice.value
	}
	public get isDesktopLikeMobileDevice() {
		return this._isDesktopLikeMobileDevice.value
	}


	private _closedReasonMessage = ref('')
	public get closedReasonMessage() {
		return this._closedReasonMessage.value
	}

	private _useHd = ref(false)
	public set useHd(value: boolean) {
		this._useHd.value = value
		if (this.stream) {
			if (!this.stream.isSupportHDVideo()) {
				this._useHd.value = false
			}
		}
	}
	public get useHd() {
		return this._useHd.value
	}

	private _activeCamera = ref(undefined as string | undefined);
	private _pendingCameraFlipNotification: { senderId: string; requestId: string; onSuccess?: () => void; onFailure?: () => void } | null = null;
	private _settingActiveCamera = false
	private _lastCameraCount: number | null = null
	private _pendingParticipantUpdates = new Map<string, Partial<MeetingParticipant>>() // Store pending updates for participants not yet in list
	public set activeCamera(value: string) {
		if(this._settingActiveCamera){
			return
		}
		this._settingActiveCamera = true
		this._activeCamera.value = undefined
		this.consoleLog('setting active camera', value)
		const notification = this._pendingCameraFlipNotification
		this._pendingCameraFlipNotification = null 
		
		if (this.stream && this.stream.isCapturingVideo()) {
			this.stream.switchCamera(value).then(() => {
				this.consoleLog('switched active camera to ' + value)
				this._activeCamera.value = value
				this.fire(MeetingEvent.CameraChanged, { camera: value })
				this._isPrimaryCamera.value = this.isFirstCamera()
				// Send success notification if this was a requested switch
				if (notification) {
					if (notification.onSuccess) {
						notification.onSuccess()
					}
				}
			}).catch((error) => {
				this.consoleLog('error switching camera', error)
				// Send failure notification if this was a requested switch
				if (notification) {
					if (notification.onFailure) {
						notification.onFailure()
					}
				}
			}).finally(() => {
				this._settingActiveCamera = false
			})
		} else {
			this._activeCamera.value = value
			this._isPrimaryCamera.value = this.isFirstCamera()
			this._settingActiveCamera = false
			// Send success notification if this was a requested switch
			if (notification) {
				if (notification.onSuccess) {
					notification.onSuccess()
				}
			}
		}
	}
	public get activeCamera(): string | undefined {
		return this._activeCamera.value
	}

	private _activeMicrophoneLabel = ref(undefined as string | undefined);
	public get activeMicrophoneLabel(): string | undefined {
		return this._activeMicrophoneLabel.value
	}

	private _activeMicrophone = ref(undefined as string | undefined);
	private _settingActiveMicrophone = false
	public set activeMicrophone(value: string) {
		if(this._settingActiveMicrophone){
			return
		}
		this._settingActiveMicrophone = true
		this._activeMicrophone.value = undefined
		this._activeMicrophoneLabel.value = undefined
		if (this.stream) {
			this.stream.switchMicrophone(value).then(() => {
				this.consoleLog('switched microphone', value)
				this._activeMicrophone.value = value
				// set the label too
				const microphone = this.microphones.find((m) => m.deviceId === value)
				if(microphone){
					this.consoleLog('switched microphone', value, microphone.label)
					this._activeMicrophoneLabel.value = microphone.label
				}
			}).catch((error) => {
				this.consoleLog('error switching microphone', error)
			}).finally(() => {
				this._settingActiveMicrophone = false
			})
		}else {
			this._activeMicrophone.value = value
			this._activeMicrophoneLabel.value = this.microphones.find((m) => m.deviceId === value)?.label
			this._settingActiveMicrophone = false
		}
	}
	public get activeMicrophone(): string | undefined { 
		return this._activeMicrophone.value
	}

	private _activeAudioSpeaker = ref('');
	public set activeAudioSpeaker(value: string) {
		this._activeAudioSpeaker.value = ''
		if (this.stream) {
			this.stream.switchSpeaker(value).then(() => {
				this.consoleLog('switched speaker', value)
				this._activeAudioSpeaker.value = value
			}).catch((error: any) => {
				this.consoleLog('error switching speaker', error)
			})
		}
	}
	public get activeAudioSpeaker() {
		return this._activeAudioSpeaker.value
	}

	private _state = ref(MeetingConnectionState.None)
	public get state() {
		return this._state.value
	}

	private _mediaState = ref(MediaState.None)
	private mediaWarning = ref('')
	public get mediaWarningMessage() {
		return this.mediaWarning.value
	}

	/**
     * Sorts an array of user objects by userId, then flattens them by userGuid,
     * keeping properties from objects with larger userIds when there are conflicts.
     * 
     * @param {Array} users - Array of user objects with userId and userGuid properties
     * @returns {Array} - Flattened array of user objects with unique userGuids
     */
    private sortAndFlattenUsers(users: MeetingParticipant[]): MeetingParticipant[] {
        // First, sort the array by userId in ascending order
        const sortedUsers = [...users].sort((a, b) => parseInt(a.userId,10) - parseInt(b.userId));
        
        // Use a Map to track the latest version of each unique userGuid
        const guidMap = new Map();
        
        // Process each user, with later users (higher userIds) overwriting earlier ones
        for (const user of sortedUsers) {
        const { userGuid } = user;
        
        if (!guidMap.has(userGuid)) {
            // First time seeing this GUID, add the entire object
            guidMap.set(userGuid, { ...user });
        } else {
            // We've seen this GUID before, merge and keep properties from the newer user (higher userId)
            const existingUser = guidMap.get(userGuid);
            
            // Since we've sorted by userId, the current user will always have a userId >= existing
            guidMap.set(userGuid, { ...existingUser, ...user });
        }
        }
        
        // Convert the Map values back to an array
        return Array.from(guidMap.values());
    }
    private _participants = ref([] as MeetingParticipant[])
    public get participants(): MeetingParticipant[] {
        // any participants that are not in the _usersInFailover list
        return this.sortAndFlattenUsers(this._participants.value as MeetingParticipant[]).filter(p => !(this._usersInFailover.value.some(x=> '' + x == p.userId))) as MeetingParticipant[]
    }

	public get me(){
		if (!this.client) {
			return null
		}
		const userId = this.getUserId()
		if (!userId) {
			return null
		}
		return this.participants.find((p) => p.userId === userId)
	}

	public isUserInParticipants(userId: string): boolean {
		if (!this.client) {
			return false
		}
		return this.participants.some((p) => p.userId == userId)
	}


	private _cameraList = ref([] as MediaDevice[])
	public get cameras(): MediaDevice[] {
		return this._cameraList.value
	}

	private _microphoneList = ref([] as MediaDevice[])
	public get microphones(): MediaDevice[] {
		return this._microphoneList.value
	}

	private _audioSpeakerList = ref([] as MediaDevice[])
	public get audioSpeakers(): MediaDevice[] {
		return this._audioSpeakerList.value
	}

	private _isCameraRequired = ref(false)
	public set isCameraRequired(value: boolean) {
		this._isCameraRequired.value = value
	}
	public get isCameraRequired() {
		return this._isCameraRequired.value
	}

	private _isCameraAvailable = ref(false)
	public get isCameraAvailable() {
		return this._isCameraAvailable.value
	}

	private _isHostOrManager = ref(false)
	public get isHostOrManager() {
		return this._isHostOrManager.value
	}

	private _isHost = ref(false)
	public get isHost(): boolean {
		return this._isHost.value
	}

	public isUserAHostOrManager(userId: string): boolean {
		if (!this.client) {
			this.consoleLog('Client not initialized in isUserAHostOrManager')
			return false
		}
		try{
			const user = this.client.getAllUser().find((u) => '' + u.userId == userId)
			if (!user) {
				return false
			}
			return user.isHost || user.isManager
		}catch(e){
			this.consoleLog('Error getting user in isUserAHostOrManager', e)
			return false
		}
	}

	public override iAmHostOrManager(): boolean {
		return this.isUserAHostOrManager(this.getUserId())
	}

	private _defaultMicrophoneOff = ref(false as boolean | undefined)
	public get defaultMicrophoneOff(): boolean | undefined {
		return this._defaultMicrophoneOff.value
	}
	public set defaultMicrophoneOff(value: boolean | undefined) {
		this._defaultMicrophoneOff.value = value
	}

	/// only applies on startup
	private _defaultCameraOff = ref(false as boolean | undefined)
	public get defaultCameraOff(): boolean | undefined {
		return this._defaultCameraOff.value
	}
	public set defaultCameraOff(value: boolean | undefined) {
		this._defaultCameraOff.value = value
	}

	private _isMicrophoneMuted = ref(undefined as boolean | undefined)
	private _isMutingMicrophone = false
	public get isMicrophoneMuted(): boolean | undefined {
		return this._isMicrophoneMuted.value
	}

	private _microphoneMutingTimeout = 0
	public set isMicrophoneMuted(value: boolean) {
		if (!this.stream) {
			return
		}
		if(this._isMutingMicrophone){
			return
		}
		this._isMutingMicrophone = true
		this._isMicrophoneMuted.value = undefined

		// bug in zoom's sdk, the mute/unmute can fail without an error, so we set a timeout to reset the state
		clearTimeout(this._microphoneMutingTimeout)
		this._microphoneMutingTimeout = window.setTimeout(() => {
			if (this._isMutingMicrophone) {
				this.consoleWarn('Microphone muting took too long, resetting state')
				this._isMicrophoneMuted.value = true
				this._isMutingMicrophone = false
			}
		}, 15000)
		if (value) {
			this.stream.muteAudio().then(() => {
				this.consoleLog('muted audio')
			}).catch((error) => {
				this.consoleError('error muting audio', error)
				
			}).finally(() => {
				clearTimeout(this._microphoneMutingTimeout)
				if(this.stream){
					this._isMicrophoneMuted.value = this.stream.isAudioMuted()
				}else{
					this._isMicrophoneMuted.value = true
				}
				this._isMutingMicrophone = false
			})
		} else {
			
			this.stream.unmuteAudio().then(() => {
				this.consoleLog('unmuted audio')
			}).catch((error) => {
				this.warn('We were unable to unmute your microphone. Please check your network, or refresh and try again.')
				this.consoleError('error unmuting audio', error)
				
			}).finally(() => {
				clearTimeout(this._microphoneMutingTimeout)
				if(this.stream){
					this._isMicrophoneMuted.value = this.stream.isAudioMuted()
				}else{
					this._isMicrophoneMuted.value = false
				}
				this._isMutingMicrophone = false
			})
		}
	}

	private _isCameraMuted = ref(undefined as boolean | undefined)
	private _isMutingCamera = false
	public get isCameraMuted(): boolean | undefined {
		return this._isCameraMuted.value
	}

	private _cameraMutingTimeout = 0
	public set isCameraMuted(value: boolean) {
		if (!this.stream) {
			return
		}
		if (value === false && this.stream.isCapturingVideo()) {
			this.consoleLog('camera is already on, not un-muting, but changing internal state')
			this._isCameraMuted.value = false
			this.detachUserPlaceholder(this.getUserIdNumber())
			this.attachUserVideo(this.getUserIdNumber())
			return
		}
		if (value === true && !this.stream.isCapturingVideo()) {
			this.consoleLog('camera is already off, not muting, but changing internal state')
			this._isCameraMuted.value = true
			this.detachUserVideo(this.getUserIdNumber())
			this.attachUserPlaceholder(this.getUserIdNumber())
			return
		}
		if(this._isMutingCamera){
			return
		}
		this._isMutingCamera = true
		
		// bug in zoom's sdk, the mute/unmute can fail without an error, so we set a timeout to reset the state
		clearTimeout(this._cameraMutingTimeout)
		this._cameraMutingTimeout = window.setTimeout(() => {
			if (this._isMutingCamera) {
				this.consoleWarn('Camera muting took too long, resetting state')
				this._isCameraMuted.value = true
				this._isMutingCamera = false
			}
		}, 15000)

		this._isCameraMuted.value = undefined
		this.consoleLog('muting camera', value)
		if (value) {
			this.stream.stopVideo().then(() => {
				this.consoleLog('mute: stopped video')
				this.detachUserVideo(this.getUserIdNumber())
				this.attachUserPlaceholder(this.getUserIdNumber())
			}).catch((error) => {
				this.consoleError('mute: error stopping video', error)
				this.warn('We were unable to stop your camera. Please check your network, or refresh and try again.')
			}).finally(() => {
				if(this.stream){
					this.consoleLog('mute: checking if camera is muted', this.stream.isCapturingVideo())
					this._isCameraMuted.value = !(this.stream.isCapturingVideo())
				}else{
					this.consoleLog('mute: stream is not initialized, setting camera muted to true')
					this._isCameraMuted.value = true
				}
				this._isMutingCamera = false
				clearTimeout(this._cameraMutingTimeout)
			})
		} else {

			this.startVideo('auto').then(() => {
				this.consoleLog('mute: started video')
				this.detachUserPlaceholder(this.getUserIdNumber())
				this.attachUserVideo(this.getUserIdNumber())
			}).catch((error) => {
				this.consoleError('mute: error starting video', error)
				this.warn('We were unable to start your camera. Please check your network, or refresh and try again.')
			}).finally(() => {
				if(this.stream){
					this.consoleLog('mute: checking if camera is muted', this.stream.isCapturingVideo())
					this._isCameraMuted.value = !(this.stream.isCapturingVideo())
				}else{
					this.consoleLog('mute: stream is not initialized, setting camera muted to true')
					this._isCameraMuted.value = true
				}
				this._isMutingCamera = false
				clearTimeout(this._cameraMutingTimeout)
			})
		}
	}

	private async startAudio(): Promise<"" | ExecutedFailure> {
		if (!this.stream) {
			return Promise.reject('Stream not initialized');
		}
		// i'm tired of being asked to test this
		// with the original sound option, so i'm adding it here
		let result: "" | ExecutedFailure;
		if(window.location.href.includes('originalSound=true')){
			result = await this.stream.startAudio({
				originalSound: true
			});
		}else{
			/*
			// Leave this in, simulates Safari desktop 16.5
			const e = new Error('Your browser is not supported. Please use a modern browser to join the meeting.')
			e.errorCode = 6013
			throw e*/
			result = await this.stream.startAudio();
		}
		
		await this.handleDefaultMicrophoneSetup();

		await this.handleDefaultSpeakerSetup();

		this._audioEverStarted.value = true
		
		return result;
	}
	
	private async getAudioInputDevices(): Promise<MediaDeviceInfo[]> {
		const devices = await navigator.mediaDevices.enumerateDevices();
		return devices.filter(device => device.kind === 'audioinput');
	}
	
	private buildAudioParams(microphones: MediaDeviceInfo[]): AudioOption {
		const params: AudioOption = {};
		
		// Use preferred microphone if available and valid
		if (this._activeMicrophone.value) {
			const isValidMicrophone = microphones.some(mic => 
				mic.deviceId === this._activeMicrophone.value
			);
			
			if (isValidMicrophone) {
				params.microphoneId = this._activeMicrophone.value;
			}
		}
		
		return params;
	}
	
	private async handleDefaultMicrophoneSetup(): Promise<void> {
		// Set default microphone only on initial startup without active microphone
		// Must come after startAudio call due to iOS permission requirements
		if (this.isInitialized()) {
			this.consoleLog('skipping default microphone setup')
			return;
		}
		
		try{
			if(this._preferredMicrophoneId.value){
				this.consoleLog('preferred microphone set, setting default microphone to ' + this._preferredMicrophoneId.value)
				await this.stream?.switchMicrophone(this._preferredMicrophoneId.value);
				
			}else{
				// let the sdk handle it
				this.consoleLog('no preferred microphone set, skipping default microphone setup')
			}
		}catch(e){
			this.consoleLog('error setting default microphone, ignoring', e)
		}
	}

	private async handleDefaultSpeakerSetup(): Promise<void> {
		// Set default speaker only on initial startup without active speaker
		if (this.isInitialized()) {
			this.consoleLog('skipping default speaker setup')
			return;
		}
		if(!this.stream){
			this.consoleLog('stream not initialized, skipping default speaker setup')
			return;
		}

		try{
			if(this._preferredSpeakerId.value){
				this.consoleLog('preferred speaker set, setting default speaker to ' + this._preferredSpeakerId.value)
				await this.stream?.switchSpeaker(this._preferredSpeakerId.value);
			}else{
				this.consoleLog('no preferred speaker set, skipping default speaker setup')
			}
		}catch(e){
			this.consoleLog('error setting default speaker, ignoring', e)
		}
	}

	// on startup, this is the default order of microphones we search for; failing that, we just take the first one
	private _defaultDevicesPreference = []// ['airpods','bluetooth']
	
	private getDefaultDeviceId(devices: {deviceId: string, label: string}[], deviceType: string): string {
		this.consoleLog(`getting default ${deviceType} from list`, devices.map((device) => device.deviceId + ':' + device.label).join(', '))
		
		for(const device of devices){
			for(const defaultDevice of this._defaultDevicesPreference){
				if(device.label.toLowerCase().includes(defaultDevice)){
					this.consoleLog(`found default ${deviceType}`, device.label)
					return device.deviceId
				}
			}
		}
		
		// if we didn't find a default device, just return the first one
		return devices[0].deviceId
	}

	private getDefaultMicrophoneId(microphones: {deviceId: string, label: string}[]): string {
		return this.getDefaultDeviceId(microphones, 'microphone')
	}

	private getDefaultSpeakerId(speakers: {deviceId: string, label: string}[]): string {
		return this.getDefaultDeviceId(speakers, 'speaker')
	}

	// starts video with virtual background if set or blur if set
	// if none is set, starts with no background or blur
	// if auto is set, attempts virtual background then blur, accorindg to what is supported, falling back to normal
	private async startVideo(mode: 'virtual' | 'blur' | 'auto' | 'none'): Promise<ExecutedResult> {
		if (!this.stream) {
			return Promise.reject('Stream not initialized')
		}
		this.consoleLog('starting video for self - user ' + this.getUserId(), mode, this._isVirtualBackgroundSupported.value)
		let params = {} as CaptureVideoOption

		if ((mode == 'virtual' || (mode == 'auto' && this._isVirtualBackgroundActive.value)) && this._virtualBackgroundUrl.value && this._isVirtualBackgroundSupported.value) {
			params = { virtualBackground: { imageUrl: this._virtualBackgroundUrl.value } }
		} else if ((mode == 'blur' || (mode == 'auto' && this._isBlurActive.value)) && this._isBlurSupported.value) {
			params = { virtualBackground: { imageUrl: 'blur' } }
		} 
		if(this.isMobileDevice){
			params.originalRatio = true
		}
		if(this._useHd.value && this.stream.isSupportHDVideo()){
			this.consoleLog('using HD video')
			params.hd = true
		}else{
			this.consoleLog('NOT HD video')
			params.hd = false
		}
		// set the active camera up
		if (this._activeCamera.value) {
			this.consoleLog('active camera is set to ' + this._activeCamera.value)
			const activeExists = this.getCameraList().some((camera) => {
				return camera.deviceId == this._activeCamera.value
			})
			if (!activeExists) {
				this.consoleLog('active camera ' + this._activeCamera.value + ' not in list, list is: ', this.getCameraList().map((camera) => camera.deviceId).join(', '))
				this._activeCamera.value = this._preferredCameraId.value

				const preferredExists = this.getCameraList().some((camera) => {
					return camera.deviceId == this._activeCamera.value
				})
				if(!preferredExists){
					this._activeCamera.value = undefined
				}
			}
			if (this._activeCamera.value) {
				params.cameraId = this._activeCamera.value
			}
		} else {
			this.consoleLog('no active camera set')
		}
		this.consoleLog('starting video with params', params)
		
		const result = await this.stream.startVideo(params)
		// if we successfully started video, then camera is available
		this._isCameraAvailable.value = true
		// if we have a preferred camera id, and the meeting is not initialized, switch to the preferred camera
		if(this._preferredCameraId.value && !this._isInitialized.value){
			try{
				// if the active camera is already set to the preferred camera, we don't need to switch it
				// this just avoids a flicker
				if(this.stream.getActiveCamera() == this._preferredCameraId.value){
					this.consoleLog('active camera is already set to preferred camera, skipping switch camera')
				}else{
					// check if the preferred camera is in the list of cameras
					const cameras = this.getCameraList()
					const preferredCameraExists = cameras.some(camera => camera.deviceId == this._preferredCameraId.value)
					if(!preferredCameraExists){
						this.consoleLog('preferred camera (' + this._preferredCameraId.value + ') not in list of cameras, skipping switch camera')
					}else{
						await this.stream?.switchCamera(this._preferredCameraId.value)
					}
				}
			}catch(e){
				this.consoleError('error switching camera', e)
			}
		}
		
		return result
	}

	private getMyScreenshareVideoElement(): HTMLVideoElement {
		const video = document.createElement('video')
		video.setAttribute('id', 'my-screen-share-content-video')
		video.classList.add('screenshare')
		video.classList.add('my-screenshare')
		//video.setAttribute('node-id', 'my-ss-v-' + this.getUserIdNumber())
		return video
	}

	private getMyScreenshareCanvasElement(): HTMLCanvasElement {
		const canvas = document.createElement('canvas')
		canvas.setAttribute('id', 'my-screen-share-content-canvas')
		canvas.classList.add('screenshare')
		canvas.classList.add('my-screenshare')
		//canvas.setAttribute('node-id', 'my-ss-c-' + this.getUserIdNumber())
		return canvas
	}

	private getOtherScreenshareCanvasElement(userId: number): HTMLCanvasElement {
		const canvas = document.createElement('canvas')
		canvas.setAttribute('id', 'users-screen-share-content-canvas')
		canvas.classList.add('screenshare')
		canvas.classList.add('other-screenshare')
		canvas.setAttribute('data-user-id', '' + userId)
		//canvas.setAttribute('node-id', 'other-ss-c-' + userId)
		return canvas
	}

	private _isScreenshareVideoElementAttached = ref(false)
	public get isAnyScreenShareActive(): boolean {
		return this._isScreenshareVideoElementAttached.value
	}

	private _isPrimaryCamera = ref(false as boolean)
	public get isPrimaryCamera(): boolean {
		this.consoleLog('isPrimaryCamera', this._isPrimaryCamera.value)
		return this._isPrimaryCamera.value
	}

	private _iAmScreenSharing = ref(false as boolean | undefined)
	public get iAmScreenSharing(): boolean | undefined {
		return this._iAmScreenSharing.value
	}

	private _isSettingScreenShare = false
	public set iAmScreenSharing(value: boolean) {
		if (!this.stream) {
			this.consoleLog('Stream not initialized in iAmScreenSharing')
			return
		}
		if (!this.client) {
			this.consoleLog('Client not initialized in iAmScreenSharing')
			return
		}
		if(this._isSettingScreenShare){
			return
		}

		this._isSettingScreenShare = true
		if (value) {
			this.consoleLog('starting screen share')
			this._iAmScreenSharing.value = undefined;

			// no audio option for screenshare
			const screenshareOptions = {
				hideShareAudioOption: true,
			} as ScreenShareOption

			const e = this.stream.isStartShareScreenWithVideoElement() ?
				this.getMyScreenshareVideoElement() :
				this.getMyScreenshareCanvasElement()

			this.stream
				.startShareScreen(e, screenshareOptions)
				.then(() => {
					//this.attachScreenshareVideo(e, this.getUserIdNumber())
					this.sendCommandFast(CommandType.ScreenshareStarted)
					this._isScreenshareVideoElementAttached.value = true
					this._screenShareElement.value = e
					this._iAmScreenSharing.value = true
					this.fire(MeetingEvent.ScreenshareAdded, { userId: this.getUserId(), element: e } as ElementAddedEventArgs)

					// If I'm the LP owner, deactivate LP when I start screensharing
					// If someone else starts screensharing, the owner will receive ScreenshareStarted command and deactivate
					if (this.isLaserPointerOwner()) {
						this.deactivateLaserPointer()
					}
				})
				.catch((error) => {
					if(error.errorCode == 6200){
						this.consoleLog('starting share screen - user cancelled')
					}else{
						this.consoleError('Error starting share screen', error)
					}
					this._iAmScreenSharing.value = false
				}).finally(() => {
					this._isSettingScreenShare = false
				})
		} else {
			this.stream.stopShareScreen().then(() => {
				this._iAmScreenSharing.value = false
			}).catch((error) => {
				this.consoleError('Error stopping share screen', error)
				this._iAmScreenSharing.value = false
			}).finally(() => {
				this._isSettingScreenShare = false
				this.detachScreenshareVideo(this.getUserIdNumber(), true)
			})
		}
	}

	private _isScreenShareSupported = ref(false)
	public get isScreenShareSupported() {
		return this._isScreenShareSupported.value
	}

	private _isCameraSupported = ref(false)
	public get isCameraSupported() {
		return this._isCameraSupported.value
	}

	private _isVirtualBackgroundSupported = ref(undefined as boolean | undefined)
	public get isVirtualBackgroundSupported(): boolean | undefined {
		return this._isVirtualBackgroundSupported.value
	}
	public set isVirtualBackgroundSupported(value: boolean | undefined) {
		this._isVirtualBackgroundSupported.value = value
	}

	private _isBlurSupported = ref(undefined as boolean | undefined)
	public get isBlurSupported(): boolean | undefined {
		return this._isBlurSupported.value
	}
	public set isBlurSupported(value: boolean | undefined) {
		this._isBlurSupported.value = value
	}

	private _isRecording = ref(false as boolean | undefined)
	public get isRecording(): boolean | undefined {
		return this._isRecording.value
	}

	private _shouldShowRecordingStartError = ref(false as boolean | undefined)
	public get shouldShowRecordingStartError(): boolean | undefined {
		return this._shouldShowRecordingStartError.value
	}
	public set shouldShowRecordingStartError(value: boolean) {
		this._shouldShowRecordingStartError.value = value
	}

	public get sessionId() {
		if (!this.client) {
			return 'initializing...'
		}
		return this.client.getSessionInfo().sessionId
	}

	private _isSettingRecording = false
	public set isRecording(value: boolean) {
		if (!this.client) {
			this.consoleLog('Client not initialized in isRecording')
			return
		}
		if(this._isSettingRecording){
			return
		}
		const mc = this.mediaClient
		if(!mc){
			this.consoleLog('MediaClient not initialized in isRecording')
			return
		}
		this._isSettingRecording = true
		if (!value) {
			this.consoleLog('User manually stopped recording - isRecording set to false')
			this._isRecording.value = undefined;
			mc.stopRecording(this.client?.getSessionInfo().topic || '', this.sessionId)
				// do nothing, wait for status to return from zoom
				.catch((err) => {
					this.consoleError('Error stopping recording', err)
					this._isRecording.value = false;
				})
				.finally(() => {
					this._isSettingRecording = false
				})
		}
		else {
			this._isRecording.value = undefined;
			this._startRecordingWithRetry(mc);
		}
	}

	private async _startRecordingWithRetry(mc: any) {
		const delays = [1000, 2000, 4000]; 
		let lastError;
		
		for (let attempt = 0; attempt <= delays.length; attempt++) {
			try {
				this.consoleLog(`Recording start attempt ${attempt + 1}/${delays.length}`);
				await mc.startRecording(this.client?.getSessionInfo().topic || '', this.sessionId);
				this.consoleLog(`Recording started successfully on attempt ${attempt + 1}`);
				this._isSettingRecording = false;
				return; 
			} catch (error) {
				lastError = error;
				this.consoleError(`Recording start failed on attempt ${attempt + 1}:`, error);
				
				if (attempt < delays.length) {
					this.consoleLog(`Retrying in ${delays[attempt]}ms...`);
					await new Promise(resolve => setTimeout(resolve, delays[attempt]));
				}
			}
		}
		
		this.consoleError(`Recording start failed after ${delays.length + 1} attempts`, lastError);
		this._isRecording.value = false;
		this._shouldShowRecordingStartError.value = true;
		this._isSettingRecording = false;
	}

	public get isConnected() {
		return this._state.value === MeetingConnectionState.Connected
	}

	public get isWaiting() {
		return this._state.value === MeetingConnectionState.Connected && this.participants.length <= 1
	}

	public get isInitializing() {
		return this._state.value === MeetingConnectionState.Initializing
	}

	public get isReady() {
		const ready = this._state.value === MeetingConnectionState.Connected && this.participants.length > 1 && !this._isDestroyedPermanently.value
			&& (this._mediaState.value === MediaState.Accessed || this._mediaState.value === MediaState.Warning)
		return ready || false
	}

	public get isConnecting() {
		return this._state.value === MeetingConnectionState.Connecting
	}

	public get isAccessingMedia() {
		return this._mediaState.value === MediaState.AccessingDevice
	}

	public get isPermissionDenied() {
		return this._mediaState.value === MediaState.PermissionDenied
	}

	public get isMediaReady() {
		return this._mediaState.value === MediaState.Accessed || this._mediaState.value === MediaState.Warning
	}

	public get isWarningMedia() {
		return this._mediaState.value === MediaState.Warning
	}
	public set isWarningMedia(value: boolean) {
		if (value) {
			this._mediaState.value = MediaState.Warning
		} else {
			this._mediaState.value = this._previousMediaState.value
		}
	}

	public get isReconnecting() {
		return this._state.value === MeetingConnectionState.Reconnecting
	}

	public get hasFailed() {
		return this._state.value === MeetingConnectionState.Fail
	}

	public get isClosed() {
		return this._state.value === MeetingConnectionState.Closed
	}

	public get isInitStarted() {
		return this.initStarted.value
	}
	private _id = uuidv4()
	public get id() {
		return this._id
	}

	private setMobileDevice() {
		const deviceInfo = Meeting.detectMobileDevice()
		this._isMobileDevice.value = deviceInfo.isMobileDevice
		this._isDesktopLikeMobileDevice.value = deviceInfo.isDesktopLikeMobileDevice
	}

	private _resizeHandler = this.setMobileDevice.bind(this);
	private _pingUrl = ref('')

	constructor(pingUrl: string = '') {
		super()
		this._pingUrl.value = pingUrl
		this.consoleLog('ZoomMeeting constructor - ' + this._id)
		this.setMobileDevice()

		window.addEventListener('resize', this._resizeHandler)
	}

	private consoleLog(message: string, ...args: any[]) {
		if(this.consoleLogChannel){
			//console.re.log('ZoomMeeting - ' + this.id + ' - ' + message, ...args)
		}else{
			console.log('Meeting - ' + this.sessionId + ' - ' + message, ...args)
		}
	}

	private consoleWarn(message: string, ...args: any[]) {
		if(this._isDestroying.value){
			console.log('logging warning while destroying meeting, ignoring', message, ...args)
			return
		}
		if(this.consoleLogChannel){
			//console.re.warn('ZoomMeeting - ' + this.id + ' - ' + message, ...args)
		}else{
			console.warn('Meeting - ' + this.sessionId + ' - ' + message, ...args)
		}
	}

	private consoleError(message: string, error: any) {
		if(this._isDestroying.value){
			console.log('logging error while destroying meeting, ignoring', message, error)
			return
		}
		if(this.consoleLogChannel){
			//console.re.error('ZoomMeeting - ' + this.id + ' - ' + message, JSON.stringify(error))
		}else{
			console.error('Meeting - ' + this.sessionId + ' - ' + message, JSON.stringify(error))
		}
	}

	private events = {} as any
	public on(event: MeetingEvent, callback: (payload: any) => void) {
		if (!this.events[event]) {
			this.events[event] = []
		}
		this.events[event].push(callback)
	}

	public fire(event: MeetingEvent, payload: any) {
		if (this.events[event]) {
			this.events[event].forEach((callback: (payload: any) => void) => {
				callback(payload)
			})
		}
	}
	public off(event: MeetingEvent, callback: (payload: any) => void) {
		if (this.events[event]) {
			this.events[event] = this.events[event].filter((cb: (payload: any) => void) => cb !== callback)
		}
	}

	public offAll() {
		for (const event in this.events) {
			if (this.events.hasOwnProperty(event)) {
				this.events[event] = []
			}
		}
	}

	public static async preload() {
		UserMedia.overrideGetUserMedia()
		ZoomVideo.preloadDependentAssets()
	}

	private static _assetProvider: 'CDN' | 'Global' | 'CN' = 'CDN'
	public static get assetProvider() {
		return this._assetProvider
	}
	public static set assetProvider(value: 'CDN' | 'Global' | 'CN') {
		if (value !== 'CDN' && value !== 'Global' && value !== 'CN') {
			throw new Error('Invalid asset provider. Must be "CDN", "Global", or "CN".')
		}
		this._assetProvider = value
	}

	public static initCounter = 0

	private fail(reason: string) {
		this._state.value = MeetingConnectionState.Fail
		this._failedReason.value = reason
	}

	private _warningTimeout = 0
	private _previousMediaState = ref(MediaState.None)
	private warn(reason: string) {
		this.mediaWarning.value = reason
		this._previousMediaState.value = this._mediaState.value
		this._mediaState.value = MediaState.Warning
		clearTimeout(this._warningTimeout)
		this._warningTimeout = window.setTimeout(() => {
			this._mediaState.value = this._previousMediaState.value
			this.mediaWarning.value = ''
		})
	}

	private _isVirtualBackgroundActive = ref(false as boolean | undefined)
	private _isVirtualBackgroundToggling = false
	public get isVirtualBackgroundActive(): boolean | undefined {
		return this._isVirtualBackgroundActive.value
	}
	public set isVirtualBackgroundActive(value: boolean) {

		// turn off blur if it is on
		this._isBlurActive.value = false

		if (this.stream != null && this.stream.isCapturingVideo()) {
			if(this._isVirtualBackgroundToggling){
				this.consoleLog('Virtual background is already being set')
				return
			}
			this._isVirtualBackgroundToggling = true

			const oldValue = this._isVirtualBackgroundActive.value
			this._isVirtualBackgroundActive.value = undefined

			this.stream.stopVideo().then(() => {
				this.startVideo(value ? 'virtual' : 'none').then(() => {
					this._isVirtualBackgroundActive.value = value
				}).catch((error) => {
					this.consoleLog('error starting video with virtual background', error)
					this.warn('We were unable to start your virtual background. We have muted your video instead.')
					this._isVirtualBackgroundActive.value = oldValue
				}).finally(() => {
					this._isVirtualBackgroundToggling = false
				})
			}).catch(() =>{
				this.consoleLog('error stopping video')
				this._isVirtualBackgroundActive.value = oldValue
			}).finally(() => {
				this._isVirtualBackgroundToggling = false
			})
		} else {
			this._isVirtualBackgroundActive.value = value
		}
	}

	private _isBlurActive = ref(false as boolean | undefined)
	public get isBlurActive(): boolean | undefined {
		return this._isBlurActive.value
	}

	private _isBlurToggling = false
	public set isBlurActive(value: boolean) {
		if (!this._isBlurSupported.value) {
			this.consoleLog('Blur not supported')
			return
		}

		// turn off virtual background if it is on
		this._isVirtualBackgroundActive.value = false
		if (this.stream && this.stream.isCapturingVideo()) {
			if(this._isBlurToggling){
				this.consoleLog('Blur is already being set')
				return
			}
			this._isBlurToggling = true

			const oldValue = this._isBlurActive.value
			this._isBlurActive.value = undefined
			this.stream.stopVideo().then(() => {
				if (!this.stream) {
					this.consoleLog('Stream not initialized in isBlurActive setter')
					return
				}
				this.startVideo(value ? 'blur' : 'none').then(() => {
					this._isBlurActive.value = value
				}).catch((error) => {
					this.consoleLog('error starting video with blur', error)
					this.warn('We were unable to blur your video. We have muted your video instead.')
					this._isBlurActive.value = oldValue
				}).finally(() => {
					this._isBlurToggling = false
				})
			}).catch(() =>{
				this.consoleLog('error stopping video')
				this._isBlurActive.value = oldValue
			}).finally(() => {
				this._isBlurToggling = false
			})
		} else {
			this._isBlurActive.value = value
		}
	}

	public nextCamera(senderId?: string, requestId?: string, onSuccess?: () => void, onFailure?: () => void) {
		// Store notification info if provided (for agent-requested camera flip)
		if (senderId && requestId) {
			this._pendingCameraFlipNotification = { senderId, requestId, onSuccess, onFailure }
		} else {
			this._pendingCameraFlipNotification = null
		}

		if (this._settingActiveCamera || !this.stream) {
			// If this is an agent-requested flip, immediately fail it
			onFailure?.();
			if (senderId && requestId) {
				this._pendingCameraFlipNotification = null
			}
			return
		}

		const cameras = this.getCameraList()
		if (cameras.length === 0) {
			// If this is an agent-requested flip, immediately fail it
			onFailure?.()
			if (senderId && requestId) {
				this._pendingCameraFlipNotification = null
			}
			return
		}
		
		const currentIndex = cameras.findIndex((camera) => camera.deviceId === this.activeCamera)
		if (currentIndex === -1) {
			this.activeCamera = cameras[0].deviceId
			return
		}
		const nextIndex = (currentIndex + 1) % cameras.length
		this.activeCamera = cameras[nextIndex].deviceId
	}

	private _autoSpotlightFunction = null as Function | null
	public set autoSpotlightFunction(handler: Function) {
		this._autoSpotlightFunction = handler
	}

	public async getSnapshot(): Promise<Blob | null> {
		if (!this.stream) {
			this.consoleLog('Stream not initialized in getSnapshot')
			return Promise.resolve(null);
		}

		// If camera is switching, wait for it to complete, then allow video stream to stabilize
		if (this._settingActiveCamera) {
			this.consoleLog("Camera is switching, waiting before taking snapshot");
			const startTime = Date.now();
			while (this._settingActiveCamera && Date.now() - startTime < 3000) {
				await new Promise((resolve) => setTimeout(resolve, 100));
			}

			if (!this._settingActiveCamera) {
				// After switch completes, wait for video stream to stabilize
				await new Promise((resolve) => setTimeout(resolve, 500));
			} else {
				this.consoleLog("Camera switch timed out, proceeding with snapshot anyway");
			}
		}
		
		if (!this.stream) {
			this.consoleLog('Stream not initialized in getSnapshot')
			return Promise.resolve(null)
		}

		const result = await this.stream.screenshotVideo(this.getUserIdNumber());
		// if we get a blob, return it
		if (result && result instanceof Blob) {
			const isMirrored = this.stream.isVideoMirrored();

			if (isMirrored) {
				// Mirror the snapshot
				return mirrorImage(result);
			}

			return result;
		}
		throw new Error("Failed to get snapshot");
  }
	
	private _disableTorch = ref(false as boolean)
	public get disableTorch() {
		return this._disableTorch.value
	}
	public set disableTorch(value: boolean) {
		this._disableTorch.value = value
	}

	private setTorchStatus() {
		if (!UserMedia.latestVideoMediaStream) {
			return false
		}
		const videoTrack = UserMedia.latestVideoMediaStream.getVideoTracks()[0];
		if (!videoTrack) {
			this.consoleLog('No video track available');
			return false;
		}
		const capabilities = videoTrack.getCapabilities();
		const hasTorch = capabilities && 'torch' in capabilities;

		this._isTorchAvailable.value = hasTorch
	}

	

	private _isTorchAvailable = ref(false as boolean)
	public get isTorchAvailable(): boolean {
		return this._isTorchAvailable.value
	}

	private _isTorchEnabled = ref(false as boolean | undefined)
	public get isTorchEnabled(): boolean | undefined {
		return this._isTorchEnabled.value
	}

	public set isTorchEnabled(value: boolean) {
		if (!UserMedia.latestVideoMediaStream) {
			return
		}
		const videoTrack = UserMedia.latestVideoMediaStream.getVideoTracks()[0];
		if (!videoTrack) {
			this.consoleLog('No video track available');
			return;
		}
		const isTorchAvailable = this.isTorchAvailable
		if (!isTorchAvailable) {
			this.consoleLog('Torch is not available on this device');
			return;
		}

		this._isTorchEnabled.value = undefined
		videoTrack.applyConstraints({ torch: value } as any).then(() => {
			this._isTorchEnabled.value = value
		}).catch((e) => {
			this.consoleError('error applying constraints', e)
		})

		// if the camera changes or is muted, the torch will be disabled
		watch(() => [this.activeCamera, this.isCameraMuted], () => {
			this.consoleLog('camera or mute changed, disabling torch')
			this._isTorchEnabled.value = false
			
		}, { once: true })
	}

	// if the active camera changes, we want to set the mirror video property
	// note that this only applies to cameras on devices when Zoom uses canvas; 
	// for video elements, we set the mirroring via css. super annoying, maybe
	// we can get rid of this later
	private setMirroring = () => {
		this.consoleLog('setting mirroring', this.isFirstCamera(), this.isDesktopDevice)
		if(this.isFirstCamera() || this.isDesktopDevice) {
			this.mirrorVideo = true
		}else{
			this.mirrorVideo = false
		}
	}

	private denyWithMediaPermission(message: string = 'We were unable to start your camera or microphone.') {
		this._mediaState.value = MediaState.PermissionDenied
		this.fail(message)
		this.client?.leave().then(() => {
			this.consoleLog('left meeting')
		}).catch((e) => {
			this.consoleError('error leaving meeting', e)
		}) // leave the meeting too
	}

	
	private initializeUsers(){
		this.consoleLog('initializing users')
		if (!this.client) {
			this.consoleLog('Client not initialized in initializeUsers')
			return
		}
		if (!this.stream) {
			this.consoleLog('Stream not initialized in initializeUsers')
			return
		}
		const users = this.client.getAllUser()
		users.forEach((user) => {
			const updatedParticipant = {} as MeetingParticipant
			this.applyProperties(updatedParticipant, user);
			this.consoleLog('initializing user, adding', updatedParticipant)
			const finalIndex = this.addParticipant(updatedParticipant)

			if (finalIndex == -1) {
				this.consoleWarn('user not added', updatedParticipant)
				return
			}

			if (user.isInFailover) {
				// ignore failover users
				this.consoleWarn('in join, user in failover', user.userId)
				return
			}

			if (user.bVideoOn) {
				this.consoleLog('the video for user', user.userId, 'is on, attaching video')
				this.attachUserVideo(user.userId)
			} else {
				this.consoleLog('the video for user', user.userId, 'is off, attaching placeholder')
				this.attachUserPlaceholder(user.userId)
			}

			if (user.sharerOn) {
				const e = this.getOtherScreenshareCanvasElement(user.userId)
				this.consoleLog('initial screenshare video for user', user.userId, e)
				this.attachScreenshareVideo(e, user.userId)
			}
		})

		const spotLighted = this.stream.getSpotlightedUserList()
		spotLighted.forEach((user) => {
			this.updateParticipant({ spotlight: true, userId: '' + user.userId })
		})
	}

	public initializeSelf() {
		this.consoleLog('initializing self')
		if (!this.client) {
			this.consoleLog('Client not initialized in initializeSelf')
			return
		}
		// TODO: resolution should adjust by user type
		const userInfo = this.client.getCurrentUserInfo()
		if (!userInfo) {
			this.consoleLog('User info not found')
			setTimeout(() => {
				this.initializeSelf()
			}, 1000);
			return
		}
		this.consoleLog('init self, user info', userInfo)
		if (userInfo.bVideoOn) {
			this.detachUserPlaceholder(userInfo.userId)
			this.attachUserVideo(userInfo.userId)
		} else {
			this.detachUserVideo(userInfo.userId)
			this.attachUserPlaceholder(userInfo.userId)
		}
	}

	private attachListeners() {
		this.attachConnectionListeners()
		this.attachUserListeners()
		this.attachPeerStateListeners()
		this.attachErrorHandlerListeners()
		this.attachMediaListeners()
		this.attachDeviceListeners()
		this.attachSpotlightListeners()
		this.attachCommandListeners()
		this.attachRecordingListeners()
		this.attachNetworkListeners()
		this.attachWindowListeners()
	}

	private _consoleLogChannel = ref('' as string)
	public get consoleLogChannel(): string {
		return this._consoleLogChannel.value
	}
	public set consoleLogChannel(value: string) {
		this._consoleLogChannel.value = value
	}

	private hasGlSupport(){
		
		// if zoom says it supports virtual background, it MIGHT not be supported still,
		// specifically if there is no webgl context available
		// so we check that too
		const canvas = document.createElement("canvas");

      	// get WebGLRenderingContext from canvas element.
      	const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");

		// make sure we have a valid WebGL context
		return gl instanceof WebGLRenderingContext
	}

	public hasVirtualBackgroundSupport() {
		if (!this.stream) {
			this.consoleLog('Stream not initialized in hasVirtualBackgroundSupport')
			return false
		}
		return this.hasGlSupport() && this.stream.isSupportVirtualBackground() && this.virtualBackgroundUrl.length > 0
	}

	public hasScreenShareSupport() {
		return this.hasGlSupport() && ZoomVideo.checkSystemRequirements().screen
	}

	public hasCameraSupport() {
		return ZoomVideo.checkSystemRequirements().video
	}

	private readBlob(b: Blob): Promise<string | ArrayBuffer | null> {
		return new Promise(function(resolve, reject) {
			const reader = new FileReader();

			reader.onloadend = function() {
				resolve(reader.result);
			};
			reader.readAsDataURL(b);
		});
	}

	private _virtualBackgroundFetched = false
	public async fetchVirtualBackgroundUrl() {
		try{
			if(!this.virtualBackgroundUrl || this._virtualBackgroundFetched){
				this.consoleLog('Virtual background URL already fetched or not set, skipping fetch')
				return
			}
			this._virtualBackgroundFetched = true
			const r = await fetch(this.virtualBackgroundUrl)
			if (!r.ok) {
				this.consoleLog('Virtual background URL is not valid', this.virtualBackgroundUrl)
				this.virtualBackgroundUrl = ''
			} else {
				this.consoleLog('Virtual background URL is valid', this.virtualBackgroundUrl)
				this.isVirtualBackgroundSupported = true
				const blob = await r.blob()
				const content = await this.readBlob(blob);
				this.virtualBackgroundUrl = content as string;
			}
		}catch(e){
			this.virtualBackgroundUrl = ''
		}
	}

	private handleUnhandledErrors() {
		// this is a workaround for a bug in the zoom sdk where the connection is lost
		// and we need to reload the page
		// we can add any other UNHANDLED errors here also.
		
		// Map of error patterns that should trigger a page reload
		const reloadErrorPatterns = [
			{
				chunks: ['undefined is not an object (evaluating', 'audio']
			}
		]
		
		const handleError = (message: string) => {
			try {
				// if the message is not a string, or the indexOf function is not a function, bail out
				if (!message || typeof message.indexOf !== 'function') {
					return
				}
				
				// Check if any reload pattern matches the error message
				const shouldReload = reloadErrorPatterns.some(pattern => 
					pattern.chunks.every(chunk => message.indexOf(chunk) > -1)
				)
				
				if (shouldReload) {
					//alert('An unexpected error occurred in your browser. Your meeting will automatically restart.')
					window.location.reload()
				}
			} catch (e) {
				console.error('error handling unhandled error', e)
			}
		}

		window.addEventListener('error', (e) => {
			console.error('error', e)
			handleError(e.message)
		})

		window.addEventListener('unhandledrejection', function(event) {
			handleError(event.reason)
		});
	}

	private forceFullShutdownWithMessage(){
		this._closedReasonMessage.value = 'Someone else has joined as a visitor. Please try again later.'
		this._isDestroying.value = true
		this._isDestroyedPermanently.value = true
		this._state.value = MeetingConnectionState.Closed

		setTimeout(() => {
			// destroy after 100ms to allow the message to send
			this.destroy(false).catch((e) => {
				this.consoleLog('error destroying meeting', e)
			})
		}, 100); 
	}

	private async clientInit(): Promise<void> {
		if (!this.client) {
			throw new Error('Zoom client is missing. Failed to initialize.')
		}

		const initOptions = { patchJsMedia: false, leaveOnPageUnload: true, stayAwake: true }
		const isAssetAccessError = (error: any) => error.errorCode === 5001 || error.reason?.includes('dependent assets')
		const assetAccessErrorMessage = "So sorry, but we are having trouble connecting you to the meeting. Please make sure you have a good internet connection and try again."
		
		// Auto-swap to the other asset provider
		const getFallbackAssetProvider = (currentProvider: string) => {
			switch (currentProvider) {
				case 'CDN': return 'Global'
				case 'Global': return 'CDN'
				case 'CN': return 'Global'
				default: return 'Global'
			}
		}

		try {
			this.consoleLog('init: initializing zoom client with asset provider - ', ZoomMeeting.assetProvider)
			await this.client.init('en-US', ZoomMeeting.assetProvider, initOptions)
			this.consoleLog('init: successfully initialized zoom client with asset provider - ', ZoomMeeting.assetProvider)
		} catch(error: any) {
			// If asset provider fails with asset access error, try fallback
			if (isAssetAccessError(error)) {
				const fallbackProvider = getFallbackAssetProvider(ZoomMeeting.assetProvider)
				this.consoleWarn(`init: initializing zoom client with asset provider - ${ZoomMeeting.assetProvider} failed, falling back to ${fallbackProvider} provider`, error)
				
				try {
					// Try fallback provider
					await this.client.init('en-US', fallbackProvider, initOptions)
					this.consoleLog(`init: successfully initialized zoom client with asset provider - ${fallbackProvider} fallback`)
				} catch(fallbackError: any) {
					// If fallback also fails
					if (isAssetAccessError(fallbackError)) {
						this.consoleError('init: initializing zoom client with both asset providers failed', fallbackError)
						throw new Error(assetAccessErrorMessage)
					}
					throw fallbackError
				}
			} else {
				// If it's not an asset access error, re-throw the original error
				throw error
			}
		}
	}

	public async init(token: string): Promise<void> {
		await super.init(token)

		this.handleUnhandledErrors()
		/*if(this.consoleLogChannel){
			consolere.connect({
				server: 'https://console.re', // optional, default: https://console.re
				channel: this.consoleLogChannel, // required
				redirectDefaultConsoleToRemote: false, // optional, default: false
				disableDefaultConsoleOutput: true, // optional, default: false
			});
		}*/

		// this is a debugging tool to force an exception to be thrown outside of our control,
		// for testing purposes. we put it in a timeout to ensure it's not
		// caught by any normal error handling, and lands in the "handleUnhandledErrors"
		// function
		if((window as any).LSForcedException){
			setTimeout(() => {
				throw new Error((window as any).LSForcedException)
			}, 1000)
		}

		ZoomMeeting.initCounter++
		this.initStarted.value = true
		this._state.value = MeetingConnectionState.Initializing

		if (!token) {
			this.fail('No authorization token could be found. Please refresh and try again.')
			throw new Error('No token provided')
		}

		

		const compatibility = ZoomVideo.checkSystemRequirements()
		if (!compatibility.audio) {
			this.consoleLog('Audio not supported')
			throw new Error('Audio is not supported on this device.')
		}

		this.client = ZoomVideo.createClient()

		const decoded = jwtDecode(token) as any

		this.consoleLog('is host: ' + decoded.role_type)
		this._isHostOrManager.value = (decoded.role_type == 1)

		/*
		left in place for debugging; useful for testing how we handle failure
		on the initial join
		if(ZoomMeeting.initCounter == 1){
			throw new Error('ZoomVideoClient is not initialized. Please check your Zoom SDK setup.')
		}
		*/

		await this.clientInit()
		this._state.value = MeetingConnectionState.Connecting

		this.attachListeners()

		try {
			await this.client.join(decoded.tpc, token, decoded.user_identity, '123');

		} catch (e: any) {
			// if this happens, the zoom fail event will fire and handle it;
			// we just bail out
			throw e
		}

		// if we are not host or manager, we need to send the visitor ejected command
		if(!this.isHostOrManager){
			const payload = {
				commandType: CommandType.VisitorEjected,
				senderId: this.getUserId(),
				senderName: this.getName()
			} as CommandPayload

			// send the command to the server
			try{
				await this.sendCommand(payload)
			}catch(e: any){
				
				if(e.errorCode == 5002){
					// this is the meeting is closed message, timing issue if we have 2 visitors coming in at the same time
					// we can just log it, because the incoming message will shut down the meeting anyway
					this.consoleLog('error sending visitor ejected command', e)
				}else{
					this.consoleError('error sending visitor ejected command', e)
				}
			}
		}

		this._mediaState.value = MediaState.AccessingDevice
		try {
			this.consoleLog('init: starting media stream')

			if(!this.stream) {
				this.consoleWarn('Stream not initialized in init')
				return Promise.reject('Stream not initialized in init')
			}

			watch(() => [this._activeCamera.value, this._isCameraMuted.value], () => {
				// if the active camera changes, we want to set the mirror video property
				if (this.isFirstCamera()) {
					this._isPrimaryCamera.value = true
				} else {
					this._isPrimaryCamera.value = false
				}
				
				// Re-apply mirroring when camera changes only if this user is on canvas mode
				if(this.iAmInCanvasMode) {
					this.setMirroring()
				}
				
				this.setTorchStatus()
				this.consoleLog('active camera or muted state changed', this.activeCamera, this._isCameraMuted.value)
			})

			this.consoleLog('init: setting vb')
			// check vb support immediately
			if (this._isVirtualBackgroundSupported.value === undefined) {
				this._isVirtualBackgroundSupported.value = this.hasVirtualBackgroundSupport()
				//await this.fetchVirtualBackgroundUrl()
			}
			// same check for blur as virtual background in zoom
			if (this._isBlurSupported.value === undefined) {
				this._isBlurSupported.value = this.hasVirtualBackgroundSupport()
			}

			this.consoleLog('init: starting audio')
			// always start the audio
			await this.startAudio()
			// should always start unmuted, but we check
			this._isMicrophoneMuted.value = this.stream.isAudioMuted()

			if(this._defaultMicrophoneOff.value && !this._isMicrophoneMuted.value){
				this.consoleWarn('default microphone off is set, but microphone is not muted, so we will mute the microphone anyway')
				this.isMicrophoneMuted = true
			}

			
			try {
				// on android in particular, we need to stop the camera or we get a violation error
				// so we clear the media access validation result to ensure we don't have any tracks left on
				// we leave the audio alone because it's not a violation error, and ensures we don't accidentally
				// mute the microphone
				try{
					const meetingConfiguration = useMeetingConfiguration()
					meetingConfiguration.clearMediaAccessValidationResult(false, true)
				}catch(e){
					this.consoleError('error clearing media access validation result', e)
				}

				this.consoleLog('init: starting video - required', this._isCameraRequired.value, 'default camera off', this._defaultCameraOff.value)
				if(this._defaultCameraOff.value && this._isCameraRequired.value){
					this.consoleWarn('default camera off is set, but camera is required, so we will start the camera anyway')
				}
				if(this._defaultCameraOff.value && !this._isCameraRequired.value){
					this._isCameraMuted.value = true
					this._isCameraAvailable.value = true // we assume the camera is available if it's not required
				}else{
					await this.startVideo('auto')
				}

			} catch (e: any) {


				// optional camera
				// if the camera is not required, we can try/catch/ignore
				this.consoleWarn('error starting video, but camera is not required; error was', e)

				// we'll give one more try to start the camera
				// with no constraints
				try{
					this.consoleLog('init: trying to start video again')
					this._activeCamera.value = undefined
					this._preferredCameraId.value = ''
					this._isVirtualBackgroundSupported.value = false
					this._isBlurSupported.value = false
					this._useHd.value = false
					await this.startVideo('none')
				}catch(e: any){
					// if for any reason we can't start the camera, we still want to set the camera available state
					// to the current state of the stream; it can possible to fail to start the camera because the camera
					// is already started, which is why we check the stream state
					this._isCameraAvailable.value = this.stream.isCapturingVideo()
					this.consoleError('error starting video again', e)
					// if the camera is still not available, we just ignore it
					if (this.isCameraRequired && !this.stream.isCapturingVideo()) {
						this.consoleError('error starting video and camera is required', e)
						throw e
					}
				}
			}

			this.consoleLog('init: setting post-camera settings')
			this._isCameraMuted.value = !(this.stream.isCapturingVideo())
			this._isPrimaryCamera.value = this.isFirstCamera()
			this._isCameraSupported.value = ZoomVideo.checkSystemRequirements().video

			this.consoleLog('support virtual background', this._isVirtualBackgroundSupported.value)
			this.consoleLog('support blur', this._isBlurSupported.value)

			this.consoleLog('init: refreshing devices')
			this.refreshDeviceList()

		} catch (e: any) {
			this.consoleError('error starting camera/mic', e)
			if(e.type == "OPERATION_TIMEOUT"){
				this.denyWithMediaPermission('A timeout was encountered while trying to start your camera or microphone. Please check your network, or refresh and try again.')
			}else if(e.errorCode == 6013){
				// safari desktop, at least 16.5, which has horrible
				// requirements in zoom for async loading 
				// of encoders, etc; https://devforum.zoom.us/t/media-sdk-change-event-never-raised-with-audio-encode-decode-values-on-ios/117444
				// we just don't support it.
				this._closedReasonMessage.value = 'Your browser is not supported. Please update your browser or use a modern browser to join the meeting.'
				this.destroy(false)
			}else{
				this.consoleWarn('error starting camera/mic - ' + e.message)
				this.denyWithMediaPermission(e.message)
			}
			return // return early
		}
		this._mediaState.value = MediaState.Accessed

		this.initializeUsers()
		this.initializeSelf()

		this.consoleLog('setting initialized')
		this._isInitialized.value = true
		this._initializedAt.value = new Date().getTime()
		this.fire(MeetingEvent.Initialized, { meeting: this })		// not particularly happy about this, but it ensures we wait for everything to settle

		/*const liveTranscriptionTranslation = this.client.getLiveTranscriptionClient();
		liveTranscriptionTranslation.startLiveTranscription().then(() => {
			liveTranscriptionTranslation.setTranslationLanguage(LiveTranscriptionLanguage.Spanish)
			liveTranscriptionTranslation.setSpeakingLanguage(LiveTranscriptionLanguage.English)
		})
		this.client.on(`caption-message`, (payload) => {
			console.log(payload)
			if(payload.language == LiveTranscriptionLanguageCode.English){
				this.captionMessage = `${payload.displayName} said: ${payload.text}`
			}else{
				this.captionMessage = `${payload.displayName} said: ${payload.text}, translated to ${payload.language}`
			}
		});*/
		try {
			const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
			cameraPermission.onchange = (event) => {
				if (cameraPermission.state === 'granted') {
					// Camera permission is granted
					this.consoleLog('Camera permission granted');
					this._isCameraAvailable.value = true
				} else if (cameraPermission.state === 'denied') {
					// Camera permission is denied
					this.consoleWarn('Camera permission denied');
					this._isCameraAvailable.value = false
					this.isCameraMuted = true
				} else {
					//Permission is prompt or default
					this.consoleLog('Camera permission status is:', cameraPermission.state);
				}
			};
		} catch (e) {

		}
	}

	private _captionMessage = ref('')
	public get captionMessage() {
		return this._captionMessage.value
	}
	public set captionMessage(value: string) {
		this._captionMessage.value = value
	}

	private _networkQuality = ref(-1 as number)
	public get networkQuality() {
		return this._networkQuality.value
	}

	private _myUplink = -1
	private _myDownlink = -1
	
	private _pingTimeout = 0 as NodeJS.Timeout | number;
	private _pingInterval = 1000

	private _badUpstreamAnywhere = computed(() => {
		return this._networkQualities.value.some((quality) => {
			return quality.uplink < 2 && quality.uplink >= 0
		})
	})
	public get badUpstreamAnywhere() {
		return this._badUpstreamAnywhere.value
	}

	private _networkQualities = ref([] as{
		userId: number,
		uplink: number,
		downlink: number
	}[]);

	private attachNetworkListeners() {
		if (!this.client) {
			this.consoleLog('Client not initialized in attachNetworkListeners')
			return
		}
		
		this._on('network-quality-change', (payload) => {
			if (!this.client) {
				this.consoleLog('Client not initialized in network-quality-change')
				return
			}
			/**
			 * `type`: 'uplink' or 'downlink'
			 * `level`: numeric value where:
			 *      0,1 = poor quality,
			 *      2 = normal,
			 *      3,4,5 = good quality.
			 */
			const { userId, type, level } = payload;
			/**
			 * We recommend using a cellular signal-style icon to represent the network quality visually.
			 */
			this.consoleLog(`User ${userId} ${type} network quality changed to ${level} `);

			if (userId == this.getUserId()) {
				if (type == 'uplink') {
					this._myUplink = level
				} else{
					this._myDownlink = level
				}
				if(this._myUplink > -1){
					this.consoleLog('my network quality', this._myUplink, this._myDownlink)
					// we always just use uplink for the network quality
					this._networkQuality.value = this._myUplink
					
					// Deactivate laser pointer if our network is bad (quality < 2)
					if (this.isLaserPointerActive && this._myUplink < 2 && this._myUplink >= 0 && this.canDeactivateLaserPointer()) {
						this.deactivateLaserPointer()
					}
				}
			}

			// find or update the network quality for the user
			const existingQuality = this._networkQualities.value.find((quality) => quality.userId === userId);
			if (existingQuality) {
				if (type == 'uplink') {
					existingQuality.uplink = level;
				} else {
					existingQuality.downlink = level;
				}
			} else {
				// If the user is not found, add a new entry
				this.consoleLog('adding network quality for user', userId, type, level)
				this.addNetworkQuality(userId, type, level);
			}
		});

		
		this.ping()
	}

	private addNetworkQuality(userId: number, type: 'uplink' | 'downlink', level: number) {
		this._networkQualities.value.push({
			userId,
			uplink: type === 'uplink' ? level : -1,
			downlink: type === 'downlink' ? level : -1
		});
	}

	private _networkFailureCount = ref(0 as number)
	public get networkFailureCount() {
		return this._networkFailureCount.value
	}
	public get networkLost() {
		return this._networkFailureCount.value > 2
	}

	private ping(){
		const controller = new AbortController();
		setTimeout(() => controller.abort(), 5000); 
		fetch(this._pingUrl.value, {
			signal: controller.signal
		})
		.then((res) => {
			// we don't care about the response, we just want to know if the request network is up
			this._networkFailureCount.value = 0
		})
		.catch((error) => {
			if (error.name === 'AbortError') {
				// ignore timeout error, this will happen on a low-bandwidth connection
				// once, but then if we truly disconnect, we'll get a proper failure
			} else {
				// network error
				this._networkFailureCount.value++
			}
		}).finally(() => {
			this._pingTimeout = setTimeout(() => {
				if(!this._isDestroyedPermanently.value){
					this.ping()
				}
			}, this._pingInterval);
		})
	}

	override handleCommand(payload: CommandPayload) {
		super.handleCommand(payload)
		switch (payload.commandType) {
			case CommandType.Backgrounded:
				this.consoleLog('backgrounded')
				if (payload.senderId != this.getUserId()) {
					this.consoleLog('backgrounded', payload.senderId)
					this.updateParticipant({
						backgrounded: true,
						userId: payload.senderId
					})
					if (this.isUserVideoOn(payload.senderId)) {
						this.detachUserVideo(parseInt(payload.senderId, 10))
						this.attachUserPlaceholder(parseInt(payload.senderId, 10))
					}
				}
				break;
			case CommandType.Unbackgrounded:
				this.consoleLog('unbackgrounded')
				if (payload.senderId != this.getUserId()) {
					this.consoleLog('unbackgrounded', payload.senderId)
					this.updateParticipant({
						backgrounded: false,
						userId: payload.senderId
					})
					if (this.isUserVideoOn(payload.senderId)) {
						this.detachUserPlaceholder(parseInt(payload.senderId, 10))
						this.attachUserVideo(parseInt(payload.senderId, 10))
					}
				}
				break;
			case CommandType.ScreenshareStarted:
				this.consoleLog('screenshare started', payload.senderId)
				if(this.iAmScreenSharing){
					this.iAmScreenSharing = false
				}

				// Deactivate laser pointer when anyone starts screensharing, but only if we're the owner
				// This ensures the owner deactivates LP and broadcasts to all participants
				if (this.isLaserPointerOwner()) {
					this.deactivateLaserPointer()
				}
				break;
			case CommandType.SetParticipantOnCanvasMode:
				this.updateParticipant({
					isOnCanvasMode: (payload as SetParticipantOnCanvasModePayload).isOnCanvasMode,
					userId: payload.senderId
				})
				break;
			case CommandType.setUserCameraCount:
				this.updateParticipant({
					cameraCount: (payload as setUserCameraCountPayload).cameraCount,
					userId: payload.senderId
				});
				break;
			case CommandType.VisitorEjected:
				// ignore
				if(this.isHostOrManager){
					return
				}
				this.consoleLog('visitor ejected', payload.senderId)
				this.forceFullShutdownWithMessage()
				break;
		}
	}


	private attachWindowListeners() {
		// Attach window listeners to handle visibility changes
		// This is only needed for mobile devices where the app can be backgrounded
		if (this.isMobileDevice) {
			this.consoleLog('attaching window listeners')
			
			// Track the timeout for visibility changes to prevent multiple handlers from running
			// This is important because visibilitychange events can fire rapidly
			let visibilityTimeout: number | null = null;
			// Track the initial visibility state when the timeout was set
			let initialVisibilityState: boolean | null = null;
			
			window.addEventListener('visibilitychange', () => {
				return
				// Skip handling if audio is already interrupted (e.g., during phone calls)
				if(this._isAudioInterrupted.value) {
					this.consoleLog('window visibility changed, but audio is interrupted, not handling')
					return
				}
				
				// Clear any existing timeout to prevent multiple handlers from executing
				// This ensures that rapid visibility changes don't cause conflicting actions
				if (visibilityTimeout) {
					//clearTimeout(visibilityTimeout);
					visibilityTimeout = null;
				}
				
				this.consoleLog('window visibility changed', document.hidden)
				
				// Store the current visibility state as the initial state for this timeout
				initialVisibilityState = document.hidden;
				
				// Set a timeout to handle the visibility change after 250ms
				// This delay prevents false triggers from brief visibility changes that can occur:
				// - When switching between browser tabs quickly
				// - During system notifications or alerts
				// - When the keyboard appears/disappears on mobile
				// - During rapid app switching
				// - Rotation of the device
				// Only sustained visibility changes (250ms+) will trigger the backgrounded/unbackgrounded actions
				visibilityTimeout = window.setTimeout(() => {
					// Check if the visibility state has changed since we set the timeout
					// If it's the same, this was a no-op (hidden then unhidden within 250ms)
					if (initialVisibilityState === document.hidden) {
						// TODO: Jerod: I'm pretty sure the "sendCommandFast" is not needed anymore, but i'm leaving it here for now because
						// we're releasing this week. I think we can remove this + the handling of it, because the
						// pause/resume video playback will handle the backgrounded/unbackgrounded state automatically since
						if (document.hidden) {
							// App has been backgrounded for at least 250ms - pause video and notify other participants
							this.sendCommandFast(CommandType.Backgrounded)
							this.pauseVideoPlayback()
						} else {
							// App has been foregrounded for at least 250ms - resume video and notify other participants
							this.sendCommandFast(CommandType.Unbackgrounded)
							this.resumeVideoPlayback()
						}
					} else {
						// Visibility state changed during the timeout - this was a no-op, do nothing
						this.consoleLog('visibility state changed during timeout - no-op detected, skipping actions')
					}
					visibilityTimeout = null;
					initialVisibilityState = null;
				}, 250);
			}, false)
		}
	}

	private _wasCameraMutedBeforeSystemInterruption = ref(false as boolean)
	private _wasMicrophoneMutedBeforeSystemInterruption = ref(false as boolean)

	private _isAudioInterrupted = ref(false as boolean)
	private _audioInterruptedTime = ref(0 as number)
	private _audioInterruptedTimeout = ref(null as NodeJS.Timeout | null)
	private _killSessionOnAudioInterruptionResume = ref(false as boolean)
	public get isAudioInterrupted() {
		return this._isAudioInterrupted.value
	}
	public set isAudioInterrupted(value: boolean) {
		this._isAudioInterrupted.value = value
		// save the time we were interrupted
		if(value){
			this._audioInterruptedTime.value = (new Date()).getTime()
		}
	}

	private _pausedAudioPlayback = ref(false as boolean)
	public pauseAudioPlayback() {
		// nada
	}

	private THIRTY_SECONDS = 30 * 1000
	private _audioEverStarted = ref(false as boolean)
	public resumeAudioPlayback() {
		// nada, handled by zoom
		this._isAudioInterrupted.value = false
	}

	private _pausedVideoPlayback = ref(false as boolean)
	public pauseVideoPlayback() {
		// nada
	}

	public resumeVideoPlayback() {
		const els = document.getElementsByTagName('video')
		Array.from(els).forEach((el: HTMLVideoElement) => {
			el.play().then(() => {
				this.consoleLog('video playback resumed')
			}).catch((e) => {
				this.consoleError('error resuming video playback', e)
			})
		})
	}

	private attachRecordingListeners() {
		if (!this.client) {
			this.consoleLog('Client not initialized in attachRecordingListeners')
			return
		}
		this._on('live-stream-status', (status) => {
			if (!this.client) {
				this.consoleLog('Client not initialized in live-stream-status')
				return
			}
			this.consoleLog('live-stream-status', status)
			if (status == LiveStreamStatus.InProgress) {
				this._isRecording.value = true
			} else if (status == LiveStreamStatus.Ended || status == LiveStreamStatus.Timeout) {
				this._isRecording.value = false
			} else if (status == LiveStreamStatus.Connecting) {
				this._isRecording.value = undefined
			}
		})
	}

	private _fastCommands = ref([] as { commandType: string; to?: string }[])
	private _maxFastCommandsToKeep = 100 // Limit to prevent memory leaks

	public override sendCommandFast(commandType: CommandType, toUserId?: string) {
		// Track fast commands for testing
		this._fastCommands.value.push({ commandType, to: toUserId })
		
		// Prevent memory leaks by limiting the array size
		if (this._fastCommands.value.length > this._maxFastCommandsToKeep) {
			// Remove oldest commands, keeping only the most recent ones
			this._fastCommands.value = this._fastCommands.value.slice(-this._maxFastCommandsToKeep)
		}
		
		const payload = {
			id: uuidv4(),
			commandType: commandType,
			senderId: this.getUserId(),
			senderName: this.getName(),
			timestamp: new Date().getTime(),
		} as CommandPayload
		this.sendCommand(payload as CommandPayload, toUserId).catch((e) => {
			this.consoleError('Error sending fast command ' + commandType, e)
		})	
	}

	// Test helper method to get fast commands
	public getFastCommands(): Array<{ commandType: string; to?: string }> {
		return this._fastCommands.value
	}

	// Test helper method to clear fast commands
	public clearFastCommands(): void {
		this._fastCommands.value = []
	}

	private sendMyCameraCount(toUserId?: string): void {
		if (!this.client || !this.stream) {
			return
		}
		
		const cameraCount = this._cameraList.value.length
		const payload: setUserCameraCountPayload = {
			commandType: CommandType.setUserCameraCount,
			id: uuidv4(),
			senderId: this.getUserId(),
			senderName: this.getName(),
			timestamp: Date.now(),
			cameraCount: cameraCount
		}
		
		this.sendCommand(payload, toUserId).catch((e) => {
			if (toUserId) {
				this.consoleError('Error sending my camera count from ' + this.getUserId() + ' to participant ' + toUserId, e)
			} else {
				this.consoleError('Error sending my camera count from ' + this.getUserId() + ' to all other participants', e)
			}
		})
	}

	public override async sendCommand(payload: CommandPayload, toUserId?: string) {
		if (!this.client) {
			this.consoleLog('Client not initialized in sendCommand')
			return
		}
		const commandChannel = this.client.getCommandClient();
		const msg = JSON.stringify(payload)
		try{
			await commandChannel.send(msg, toUserId ? parseInt(toUserId, 10) : undefined)
		}catch(e: any){
			if(e.errorCode === 7104){
				// command channel isn't ready yet, retry in 1 second
				setTimeout(() => {
					this.sendCommand(payload, toUserId).catch((e) => {
						this.consoleError('Error sending command ' + payload.commandType, e)
					})
				}, 1000)
			}
		}
	}


	private _isDestroyedPermanently = ref(false as boolean)
	public get isDestroyedPermanently() {
		return this._isDestroyedPermanently.value
	}

	private _isDestroying = ref(false as boolean)
	public get isDestroying() {
		return this._isDestroying.value
	}

	private _willRebuild = ref(false as boolean)
	public get willRebuild() {
		return this._willRebuild.value
	}
	public async destroy(willRebuild: boolean, forceFailure?: boolean): Promise<void> {
		await super.destroy(willRebuild)
		this._isDestroying.value = true
		this._willRebuild.value = willRebuild

		// Clean up fast commands to prevent memory leaks
		this.clearFastCommands()

		try{
			// Clean up all video observers to prevent memory leaks
			this._videoObservers.forEach((observer, userId) => {
				try {
					observer.disconnect();
				} catch (error) {
					console.error('Error disconnecting video observer during destroy:', error);
				}
			});
			this._videoObservers.clear();
		}catch(e){
			this.consoleLog('error cleaning up video observers', e)
		}

		// unhook all listeners for zoom
		this._offAll()
		// unhook all listeners for us
		this.offAll()

		clearInterval(this._pingTimeout)
		window.removeEventListener('resize', this._resizeHandler)
		
		try {
			this._mediaState.value = MediaState.None
			this._state.value = MeetingConnectionState.Closed

			if (this.stream) {
				await this.stream.stopVideo()
				await this.stream.stopAudio()
				await this.stream.stopShareScreen()
			}

			if (this.client?.getCurrentUserInfo()?.userId) {
				this.detachUserVideo(this.client?.getCurrentUserInfo().userId)
				this.detachUserPlaceholder(this.client?.getCurrentUserInfo().userId)
				this.detachScreenshareVideo(this.client?.getCurrentUserInfo().userId, false)
			}

			
		} catch (e:any){ 
			this.consoleLog('destroy: error stopping video/audio', e)
		}


		try {
			if (this.client) {
				if(!this.willRebuild && this.isHost){
					this.consoleLog('destroy: leaving meeting as host')
					// if we are the host, we need to end the meeting
					await this.client.leave(true)
				}else{
					await this.client.leave()
				}
			}
		} catch(e:any) {
			this.consoleLog('destroy: error leaving meeting', e)
		}

		try {
			this.consoleLog('destroying client - id ' + this.id)
			await ZoomVideo.destroyClient()
		} catch(e:any) {
			this.consoleError('destroy: error destroying client', e)
		}
		this.client = null

		if (!willRebuild) {
			this.consoleLog('destroying permanently')
			this._state.value = forceFailure ? MeetingConnectionState.Fail : MeetingConnectionState.Closed
			this._isDestroyedPermanently.value = true
			// Clear pending participant updates
			this._pendingParticipantUpdates.clear()
			UserMedia.closeAllMediaStreams()
			this.fire(MeetingEvent.DestroyedPermanently, { willRebuild: willRebuild })
		}
	}

	//private _isReconnecting = ref(false)
	attachConnectionListeners() {
		if (!this.client) {
			throw new Error('Client not initialized in attachConnectionListeners')
		}

		this._on('connection-change', (payload) => {
			if (!this.client) {
				this.consoleLog('Client not initialized in attachConnectionListeners callback')
				return
			}
			if(!payload){
				this.consoleLog('connection-change: no payload')
				return
			}
			this.consoleLog('connection-change', payload)
			if (payload.state === ConnectionState.Closed) {
				this._state.value = MeetingConnectionState.Closed
				
				// Deactivate laser pointer when our connection closes
				if (!this._isDestroying.value && this.canDeactivateLaserPointer()) {
					this.deactivateLaserPointer()
				}
				
				/**
				 * Session ended by the host or the SDK kicked the user from the session
				 * (use `payload.reason` to see why the SDK kicked the user).
				 **/
				switch (payload.reason) {
					case 'kicked by host':
						this._closedReason.value = MeetingClosedReason.KickedByHost
						break
					case 'ended by host':
						this._closedReason.value = MeetingClosedReason.EndedByHost
						this.destroy(false)
						break
					case 'expeled by host':
						this._closedReason.value = MeetingClosedReason.ExpelledByHost
						break
					default:
						this._closedReason.value = MeetingClosedReason.Unknown
						break
				}
			} else if (payload.state === ConnectionState.Reconnecting) {
				/**
				 * The client-side connection to the server was lost (e.g., when driving through a tunnel) and will attempt to reconnect for a few minutes.
				 * Alternatively, if the user joins or leaves a subsession, use `payload.subsessionName` to retrieve the subsession name.
				 */
				
				switch (payload.reason) {
					case ReconnectReason.Failover:
						this._iAmInFailover.value = true
						//this._isReconnecting.value = true
						this._reconnectReason.value = MeetingReconnectReason.Failover
						this.fire(MeetingEvent.FailoverChanged, { failover: true })
						break
					case ReconnectReason.JoinSubsession:
						this._reconnectReason.value = MeetingReconnectReason.JoinSubsession
						break
					case ReconnectReason.MoveToSubsession:
						this._reconnectReason.value = MeetingReconnectReason.MoveToSubsession
						break
					case ReconnectReason.BackToMainSession:
						this._reconnectReason.value = MeetingReconnectReason.BackToMainSession
						break
					default:
						this.consoleWarn('Unknown reconnect reason', payload.reason)
						break
				}
				
			} else if (payload.state === ConnectionState.Connected) {
				this._state.value = MeetingConnectionState.Connected
				//if(this._isReconnecting.value){
				//	this.initializeSelf()
				//}
				//this._isReconnecting.value = false
				if(this._iAmInFailover.value){
					this.consoleLog('I am in failover was set, now connected, restarting audio')
					//this.restartAudio()
				}
				this._iAmInFailover.value = false
				this.iAmScreenSharing = false // reset this on connect; weird stuff happens in the zoom sdk, and we don't get notifications
				// always set mirroring on connect/reconnect
				/**
				 * SDK connected the session successfully.
				 */
			} else if (payload.state === ConnectionState.Fail) {
				// we don't set this state because we auto-reconnect
				// this._state.value = MeetingConnectionState.Fail 
				// so the state is not actually failed, which will show the final "failed" screen with a link to restart the meeting/refresh the page
				// instead, we fire the event and let the caller handle it
				this.fire(MeetingEvent.ConnectionFailed, { payload: payload, permanent: true })
				/**
				 * SDK join session failed, use `payload.errorCode` and `payload.reason` to find out why.
				 */
				this._failedReason.value = (payload.reason as string) == 'meeting ended' ? 'The meeting has ended.' : payload.reason as string
			}
		});


	}

	private applyProperties<T extends Partial<MeetingParticipant>>(
		participant: T,
		properties: any
	) {
		for (const key in properties) {
			if (properties.hasOwnProperty(key)) {
				// @ts-ignore
				participant[key] = properties[key];
				if (key == 'userId') {
					// @ts-ignore
					participant.userId = '' + properties[key]
				}
			}
		}
	}

	private _usersInFailover = ref([] as number[])
	private handleFailover(participant: Partial<MeetingParticipant>) {
		if (participant.userId) {
			const user = this.client?.getUser(parseInt(participant.userId, 10))
			if (user) {

				// startAudio() call when coming back?
				if (user.isInFailover) {
					// users get a new id when they failover, so just track the id
					this._usersInFailover.value.push(user.userId)
					//participant.isInFailover = true
					if (user.userId == this.getUserIdNumber()) {
						// show a notification
						this.consoleLog('I am in failover', user.userId)
						//this._state.value = MeetingConnectionState.Reconnecting
						//this.fire(MeetingEvent.MyFailoverChanged, { failover: true })
						//this.fire(MeetingEvent.ConnectionFailed, { reason: 'failover' })
						// we tag this early
						this._iAmInFailover.value = true

						// if we are screen sharing, we need to stop it
						if(this.iAmScreenSharing){
							this.iAmScreenSharing = false
						}
						
						//this._participants.value = []
					} else {
						this.consoleLog('User is in failover', user.userId)
						// remove the user video and placeholder
						this.detachUserPlaceholder(user.userId)
						this.detachUserVideo(user.userId)
						this.detachScreenshareVideo(user.userId, false)
					}
					if (this.isLaserPointerActive) {
						const failingUserId = "" + user.userId;
						
						// Deactivate laser pointer if the failing user is the sender or target
						if (this.laserPointerSenderId === failingUserId || this.laserPointerTargetUserId === failingUserId) {
							if (this.canDeactivateLaserPointer()) {
								this.deactivateLaserPointer()
							}
						}
					}


				} else if (user.isInFailover === false && this._usersInFailover.value.includes(user.userId)) {
					//participant.isInFailover = false
					
					this.consoleWarn('user out of failover, removing them from the users in failover list', user.userId)
					this._usersInFailover.value = this._usersInFailover.value.filter((u) => u != user.userId)

					if (user.userGuid == this.client?.getCurrentUserInfo()?.userGuid) {
						this.consoleLog('I am out of failover', user.userId, user.userGuid, this.client?.getCurrentUserInfo()?.userGuid)
						//this._iAmInFailover.value = false

						// we don't need this anymore, it's handled in the init for a full reconnect,
						// and isn't needed for a partial reconnect
						if (this.stream?.isCapturingVideo()) {
							//this.attachUserVideo(user.userId)
						} else {
							//this.attachUserPlaceholder(user.userId)
						}
						//this._isAudioInterrupted.value = true
						if(user.userId != this.getUserIdNumber()){
							this.consoleLog('failover with user changed')
							this._iAmInFailover.value = false
						}else{
							this.consoleLog('failover with user did not change')
							this._iAmInFailover.value = false
						}
					} else {
						this.consoleLog('User is out of failover', user.userId)

						if (user.bVideoOn) {
							this.consoleLog('attaching user video')
							this.detachUserPlaceholder(user.userId)
							this.attachUserVideo(user.userId)
						} else {
							this.consoleLog('attaching user placeholder')
							this.detachUserVideo(user.userId)
							this.attachUserPlaceholder(user.userId)
						}
					}


				}
			}
		}
	}

	private removeParticipant(userId: number) {
		this._participants.value = this._participants.value.filter((p) => p.userId != '' + userId)
		this._pendingParticipantUpdates.delete('' + userId)
	}

	private addParticipant(participant: Partial<MeetingParticipant>): number {
		this.consoleLog('add participant', JSON.stringify(participant))
		if(this._participants.value.some((p) => p.userId == participant.userId)){
			// user already exists, so we need to update them
			this.consoleLog('user existed - updating instead', JSON.stringify(participant))
			// get the existing participant
			/*const existing = this._participants.value.find((p) => p.userId == participant.userId)
			const isInFailover = existing?.isInFailover
			if(isInFailover){
				// don't allow override of isInFailover
				participant.isInFailover = true
			}*/

			return this.updateParticipant(participant)
		}
		
		participant.joinTime = new Date().getTime()
		this._participants.value.push(participant as MeetingParticipant)

		const pendingUpdates = this._pendingParticipantUpdates.get(participant.userId || '')
		if (pendingUpdates) {
			this.consoleLog('Applying pending updates for participant:', participant.userId, pendingUpdates)
			this._pendingParticipantUpdates.delete(participant.userId || '')
			this.updateParticipant(pendingUpdates)
		} else {
			this.handleFailover(participant)
		}

		return this._participants.value.findIndex((p) => p.userId == participant.userId)
	}

	private didUserIdChange(participant: Partial<MeetingParticipant>): boolean {
		const existing = this._participants.value.filter((p) => p.userGuid == participant.userGuid)
		if (existing && existing.some(e=>e.userId != participant.userId)) {
			// user id changed, so we need to update the user id
			this.consoleWarn('user id changed', JSON.stringify(existing), participant.userId)
			return true
		}
		if(localStorage.getItem('zoom-test-fake-user-id-change') == 'true'){
			// this is a hack to allow us to test the user id change without actually changing the user id
			// this is useful for testing failover and other scenarios
			localStorage.removeItem('zoom-test-fake-user-id-change')
			this.consoleWarn('fake user id change', JSON.stringify(existing), participant.userId)
			return true
		}
		return false
	}

	private updateParticipant(participant: Partial<MeetingParticipant>): number {
		try {
			this.consoleLog('update participant', JSON.stringify(participant))
			const index = this._participants.value.findIndex((p) => p.userId == participant.userId)
			if (index == -1) {
				this.consoleWarn('user not found, storing update for later:', participant.userId)

				const existingPending = this._pendingParticipantUpdates.get(participant.userId || '') || {}
				this._pendingParticipantUpdates.set(participant.userId || '', {
					...existingPending,
					...participant
				})
				return -1
			}

			if (this.didUserIdChange(participant)) {
				// if the user id changed, we need to remove the old participant
				//this.removeParticipant(parseInt(this._participants.value[index].userId, 10))
				//this.fire(MeetingEvent.ConnectionFailed, { payload: {}, permanent: true })
			}

			this.handleFailover(participant)

			this.applyProperties(this._participants.value[index], participant)

			if (this._autoSpotlightFunction && this._isHostOrManager.value) {
				// invoke this function to get the spotlight id, but only if the user is the host
				const id = this._autoSpotlightFunction()
				if (id) {
					this.setSpotlight(id).catch((e) => {
						this.consoleError('Error setting spotlight', e)
					})
				}
			}
			if(participant.userId == this.getUserId() && 'isHost' in participant && participant.isHost){
				// if the user is the host, we need to update the host state
				this._isHost.value = true
			}

			// check this.client.getAllUser to make sure that this participant still exists there also
			if(this.client && participant.userId){
				const user = this.client?.getUser(parseInt(participant.userId, 10))
				if(!user){
					// user is not in the client, so we need to remove them
					this.consoleLog('user not in client, removing', participant.userId)
					this.removeParticipant(parseInt(participant.userId))
				} 
			}

			return this._participants.value.findIndex((p) => p.userId == participant.userId)
		} catch (e) {
			this.consoleError('error adding or updating participant', e)
			return -1
		}
	}

	private attachSpotlightListeners() {
		if (!this.client) {
			this.consoleLog('Client not initialized in attachSpotlightListeners')
			return
		}
		this._on('video-spotlight-change', (payload) => {
			if (!this.client) {
				this.consoleLog('Client not initialized in video-spotlight-change')
				return
			}
			if (!(payload.spotlightList)) {
				// this event sometimes fires without a spotlight list
				// we can get the spotlight list from the stream
				// this is a workaround for the issue
				const existing = this.stream?.getSpotlightedUserList()
				if (existing) {
					payload.spotlightList = existing.map((u) => { return { userId: u.userId } })
				}
			}
			if (payload.spotlightList) {
				payload.spotlightList.forEach((element: { userId: number }) => {
					this.updateParticipant({
						spotlight: true,
						userId: '' + element.userId
					} as MeetingParticipant)
				})
			}
		});
	}

	public setUserVideoState(item: ParticipantPropertiesPayload) {
		if(!('bVideoOn' in item)){
			return
		}
		this.consoleLog('setting user video state', item.userId, item.bVideoOn)
		if (item.bVideoOn) {
			this.detachUserPlaceholder(item.userId)
			this.attachUserVideo(item.userId)
		} else {
			this.detachUserVideo(item.userId)
			this.attachUserPlaceholder(item.userId)
		}
	}

	public setUserAudioState(item: ParticipantPropertiesPayload) {
		if(!('muted' in item)){
			//this.consoleLog('no audio state change')
			return
		}
		//this.consoleLog('setting user audio state', item.userId, item.muted)	
		if(item.userId == this.getUserIdNumber()){
			//this.isMicrophoneMuted = item.muted!
		}
	}

	public setLaserPointerTargetState(item: ParticipantPropertiesPayload) {
		// Sync current laser pointer state to new participant
		const currentUserId = this.getUserId()
		if (this.isLaserPointerActive && this.laserPointerSenderId === currentUserId) {
			const payload = {
				id: uuidv4(),
				commandType: CommandType.LaserPointerStateNotification,
				senderId: currentUserId,
				timestamp: Date.now(),
				isActive: this.isLaserPointerActive,
				drawingUserId: this.laserPointerSenderId,
				targetUserId: this.laserPointerTargetUserId
			}

			this.sendCommand(payload, '' + item.userId).catch((e) => {
				console.log('Error syncing laser pointer state to new participant', e)
			})
		}
	}

	private attachUserListeners() {
		if (!this.client) {
			throw new Error('Client not initialized in attachUserListeners')
		}
		this._on('user-added', (payload: ParticipantPropertiesPayload[]) => {
			if (!this.client) {
				this.consoleLog('Client not initialized in user-added')
				return
			}
			if(!payload){
				this.consoleLog('user-added - no payload')
				return
			}
			this.consoleLog('user-added', payload)
			payload.forEach((item: ParticipantPropertiesPayload) => {
				
				const updatedParticipant = {} as MeetingParticipant
				this.applyProperties(updatedParticipant, item);
				this.addParticipant(updatedParticipant)

				
				this.setUserVideoState(item)
				this.setUserAudioState(item)

				this.setLaserPointerTargetState(item)
				this.sendMyCameraCount('' + item.userId) // send my camera count to the new participant
			});
		})
		this._on('user-removed', (payload: ParticipantPropertiesPayload[]) => {
			if (!this.client) {
				this.consoleLog('Client not initialized in user-removed')
				return
			}
			this.consoleLog('user-removed', payload)
			payload.forEach((item: ParticipantPropertiesPayload) => {
				this.detachUserVideo(item.userId)
				this.detachUserPlaceholder(item.userId)
				this.detachScreenshareVideo(item.userId, false)
				this.removeParticipant(item.userId)

				if (this.isLaserPointerActive && this.laserPointerTargetUserId === item.userId.toString() && this.isLaserPointerOwner()) {
					this.deactivateLaserPointer()
				}
			});
		})

		this._on('user-updated', (payload: ParticipantPropertiesPayload[]) => {
			if (!this.client) {
				this.consoleLog('Client not initialized in user-updated')
				return
			}
			payload.forEach((item: ParticipantPropertiesPayload) => {
				// no updates for users that don't exist
				if (this._participants.value.some((p) => p.userId == '' + item.userId)) {
					const updatedParticipant = {} as MeetingParticipant

					this.applyProperties(updatedParticipant, item);
					this.consoleLog('user-updated', item, updatedParticipant)
					this.updateParticipant(updatedParticipant);
					
					
					// annoyingly, we sometimes get the peer-video-state-change event, 
					// but not always, but it seems that when we do not get it, we get this event
					// so we need to set the video state here also
					this.setUserVideoState(item)
					this.setUserAudioState(item)
					
				}
			});
		})
	}

	private attachErrorHandlerListeners() {
		if (!this.client) {
			throw new Error('Client not initialized in attachErrorHandlerListeners')
		}
		this._on('auto-play-audio-failed', () => {
			if (!this.client) {
				this.consoleLog('Client not initialized in auto-play-audio-failed')
				return
			}
			this._isAudioInterrupted.value = true
			this.warn('Audio auto-play failed.')
		})
	}

	private attachDeviceListeners() {
		if (!this.client) {
			throw new Error('Client not initialized in attachDeviceListeners')
		}
		this._on('device-change', () => {
			if (!this.stream) {
				return
			}
			this.refreshDeviceList()

		});
	}

	private _refreshDebounce = 0
	private refreshDeviceList() {
		if (!this.stream) {
			this.consoleLog('Stream not initialized in refreshDeviceList')
			return
		}
		// Get current devices from stream; note special handling for the camera to make mobile work
		const cameras = this.getCameraList()
		const mics = this.stream.getMicList();
		const speakers = this.stream.getSpeakerList();

		// Get active devices
		const activeCamera = this.stream.getActiveCamera();
		const activeMic = this.stream.getActiveMicrophone();
		const activeSpeaker = this.stream.getActiveSpeaker();

		this._activeCamera.value = activeCamera
		this._activeMicrophone.value = activeMic
		this._activeMicrophoneLabel.value = this.microphones.find((m) => m.deviceId === activeMic)?.label
		this._activeAudioSpeaker.value = activeSpeaker

		// Process cameras
		this.updateDeviceList(cameras, this._cameraList.value, activeCamera, 'camera');
		
		// Check if camera count changed and send update
		const currentCameraCount = this._cameraList.value.length
		if (this._lastCameraCount === null || this._lastCameraCount !== currentCameraCount) {
			this.sendMyCameraCount()
			this._lastCameraCount = currentCameraCount
		}

		// Process microphones
		this.updateDeviceList(mics, this._microphoneList.value, activeMic, 'microphone');

		// Process speakers
		this.updateDeviceList(speakers, this._audioSpeakerList.value, activeSpeaker, 'speaker');

		this._isCameraSupported.value = this.hasCameraSupport()
		this._isCameraAvailable.value = this.hasCameraSupport() && this._cameraList.value.length > 0
		this._isScreenShareSupported.value = this.hasScreenShareSupport()

		this._activeMicrophoneLabel.value = this.microphones.find((m) => m.deviceId === activeMic)?.label

		// this ensures that if the camera list changes, and the camera we were using is no longer available, we will mute the video
		if (!this.cameras.some((camera) => camera.deviceId === this.activeCamera)) {
			this.consoleLog('camera not available, muting video 1')
			clearTimeout(this._refreshDebounce)
			this._refreshDebounce = window.setTimeout(() => {
				this.consoleLog('camera not available, muting video 2')
				if(this.stream){
					this.isCameraMuted = !(this.stream.isCapturingVideo())
				}
			}, 75)
		}

	}

	private getCameraList(): MediaDevice[] {
		if (!this.stream) {
			this.consoleLog('Stream not initialized in getCameraList')
			return []
		}

		// per https://developers.zoom.us/docs/video-sdk/web/video/#switch-camera
		let cameras = this.stream.getCameraList();


		if (cameras.length && this.isMobileDevice) {
			cameras = [{
				label: 'Front Camera',
				deviceId: 'user'
			},
			{
				label: 'Back Camera',
				deviceId: 'environment'
			}]
		}
		return cameras
	}

	private updateDeviceList(newDevices: MediaDevice[], existingDevices: MediaDevice[], activeDevice: string, deviceType: string) {
		this.consoleLog('Updating device list', deviceType, newDevices, existingDevices, activeDevice);

		// Mark all existing devices for potential removal
		existingDevices.forEach(device => device.toRemove = true);

		// Process each new device
		newDevices.forEach(newDevice => {
			// Try to find existing device with the same ID
			const existingDevice = existingDevices.find(d => d.deviceId === newDevice.deviceId);

			if (existingDevice) {
				// Update existing device
				Object.assign(existingDevice, newDevice);
				existingDevice.toRemove = false;
				existingDevice.isActive = !!activeDevice && activeDevice === newDevice.deviceId;
			} else {
				// Add new device
				const device = { ...newDevice, isActive: !!activeDevice && activeDevice === newDevice.deviceId };
				existingDevices.push(device);
			}
		});

		// Remove devices that no longer exist using filter
		// This creates a new array with only the items that should remain
		const updatedDevices = existingDevices.filter(device => {
			if (device.toRemove) {
				return false; // Remove this device
			} else {
				delete device.toRemove; // Clean up the temporary property
				return true; // Keep this device
			}
		});

		// Clear the existing array and add all devices that should remain
		existingDevices.length = 0;
		updatedDevices.forEach(device => existingDevices.push(device));
	}

	private attachPeerStateListeners() {
		if (!this.client) {
			throw new Error('Client not initialized in attachPeerStateListeners')
		}
		this._on('peer-video-state-change', (payload) => {
			if (!this.stream) {
				return
			}
			this.consoleLog('peer-video-state-change', payload)
			if (payload.action === 'Start') {
				// a user turned on their video, render it
				this.detachUserPlaceholder(payload.userId)
				this.attachUserVideo(payload.userId)
			} else if (payload.action === 'Stop') {
				// a user turned off their video, stop rendering it
				this.attachUserPlaceholder(payload.userId)
				this.detachUserVideo(payload.userId)
			}
		})
	}

	private _hooks = {} as { [key: string]: ((payload: any) => void)[] }
	private _on = (event: string, callback: (payload: any) => void) => {
		this.client?.on(event, callback)
		if (!this._hooks[event]) {
			this._hooks[event] = []
		}
		this._hooks[event].push(callback)
	}

	private _offAll = () => {
		if (!this.client) {
			this.consoleLog('Client not initialized in _offAll')
			return
		}
		for (const event in this._hooks) {
			if (this._hooks.hasOwnProperty(event)) {
				//this.consoleLog('unhooking', event)
				this._hooks[event].forEach((callback) => {
					this.client!.off(event, callback)
				})
			}
		}
		this._hooks = {}
	}

	private attachMediaListeners() {
		if (!this.client) {
			throw new Error('Client not initialized in attachMediaListeners')
		}
		this._on("video-active-change", async (payload) => {
			if (!this.client) {
				this.consoleLog('Client not initialized in video-active-change')
				return
			}
			try {
				this.consoleLog('video-active-change', payload)
				if (payload.state === "Active") {
					this.detachUserPlaceholder(payload.userId)
					this.attachUserVideo(payload.userId)
				} else {
					if ('' + payload.userId == this.getUserId()) {
						//this._isCameraMuted.value = true
					}
					this.detachUserVideo(payload.userId)
					this.attachUserPlaceholder(payload.userId)
				}
			} catch (error: any) {
				this.consoleLog('Error in video active change - ' + error.message)
			}
		});
		this._on('video-aspect-ratio-change', (payload) => {
			if (!this.client) {
				this.consoleLog('Client not initialized in video-aspect-ratio-change')
				return
			}
			/**
			 * This event is only triggered when the render aspect ratio differs from the sending aspect ratio,
			 * so it is not expected to fire frequently.
			 */
			const { userId, aspectRatio } = payload;
			/**
			 * Locate the corresponding video-player element.
			 * This example uses a query selector; in production, you might maintain a map of user IDs to video-player elements.
			 */
			const videoPlayerElement = document.querySelector(
				`video-player[user-id="${userId}"]`
			);
			this.consoleLog('video-aspect-ratio-change', payload, videoPlayerElement)
			if (videoPlayerElement) {
				//(videoPlayerElement as any).style.aspectRatio = aspectRatio;
			}
		});
		this._on('active-media-failed', (payload) => {
			if (!this.client) {
				return
			}
			if(!payload){
				this.consoleLog('active-media-failed - no payload')
				return
			}
			this.consoleLog('active-media-failed', payload)
			const { code } = payload;
			const {
				AudioPlaybackInterrupted,
				CameraPermissionReset,
				MicrophonePermissionReset,

			} = ActiveMediaFailedCode;
			if (
				[
					MicrophonePermissionReset,
				].includes(code)
			) {
				/**
				 * Guide the user to grant the camera/microphone permission.
				 * For example, display a modal directing them to the settings page.
				 */
				this._mediaState.value = MediaState.PermissionDenied
				this.fail('We have lost access to your camera or microphone. Please check your settings.')
			} else if ([AudioPlaybackInterrupted].includes(code)) {
				/**
				 * Prompt the user to click anywhere on the page to resume audio playback.
				 * For example, use a toast notification.
				 */
				this._isAudioInterrupted.value = true
				//this._mediaState.value = MediaState.Failed
				//this.fail('We have lost access to play back audio. Please check your settings.')
			} else if ([CameraPermissionReset].includes(code)) {
				if (this.isCameraRequired) {
					this._mediaState.value = MediaState.PermissionDenied
					this.fail('We have lost access to your camera. Please check your settings.')
				} else {
					this.detachUserVideo(this.getUserIdNumber())
					this.attachUserPlaceholder(this.getUserIdNumber())
				}
			}else if (code == ActiveMediaFailedCode.WasmOutOfMemory) {
				/**
				 * Notify the user that the browser is running low on memory.
				 */
				this.warn('Your device is running low on resources. Meeting functionality may be reduced or unavailable. Please close all browsers and rejoin the meeting, or restart your device.')
			}else if (code == ActiveMediaFailedCode.VideoStreamFailed || code == ActiveMediaFailedCode.VideoConnectionFailed) {
				/**
				 * Notify the user that the video stream has failed.
				 */
				this.warn('Your device has encountered an error with the video stream. If your video is not visible, please rejoin the meeting.')
			}else if (code == ActiveMediaFailedCode.AudioStreamMuted || code == ActiveMediaFailedCode.VideoStreamMuted){
				// interruption from an external source, specifically on iOS this is when a phone call comes in
				this.consoleLog('Audio or video stream muted')
				//this.pauseAudioPlayback()
				//this.pauseVideoPlayback()
				//this.fire(MeetingEvent.AudioStreamInterruped, { })
				this._isAudioInterrupted.value = true
			}
			else{
				this.consoleWarn('active-media-failed - unknown code', code)
			}
		});

		this._on('current-audio-change', (payload) => {
			if (!this.client) {
				this.consoleLog('Client not initialized in current-audio-change')
				return
			}
			const { action, source } = payload;
			this.consoleLog('current-audio-change', payload)
			if (action === AudioChangeAction.Leave) {
				if (
					source === LeaveAudioSource.EndedBySystem ||
					source === LeaveAudioSource.MicrophoneError
				) {
					/**
					 * Display a message to the user that the audio has ended due to a system error or microphone error.
					 */
					this.consoleLog('Audio ended by system or microphone error');
					this._isMicrophoneMuted.value = true
					this._isAudioInterrupted.value = true
					
				}
			} else if (action === AudioChangeAction.Muted) {
				if (
					source === MutedSource.PassiveByMuteOne ||
					source === MutedSource.PassiveByMuteAll
				) {
					/**
					 * Display a message to the user that the audio has been muted by the host.
					 */
					this.consoleLog('Audio muted by host');
					this._isMicrophoneMuted.value = true
				}
			}
		});

		// local screen share stopped via the button in the browser
		this._on('passively-stop-share', (payload) => {
			if (!this.client) {
				this.consoleLog('Client not initialized in passively-stop-share')
				return
			}
			this._iAmScreenSharing.value = false
			if (!this.client) {
				this.consoleLog('Client not initialized in passively-stop-share')
				return
			}
			this.detachScreenshareVideo(this.getUserIdNumber(), true)
		})

		// incoming remote screen share
		this._on('active-share-change', (payload) => {
			if (!this.client) {
				this.consoleLog('Client not initialized in active-share-change')
				return
			}
			if (payload.state === 'Active') {
				const e = this.getOtherScreenshareCanvasElement(payload.userId)
				this.consoleLog('active-share-change', payload, e)
				this.attachScreenshareVideo(e, payload.userId)

			} else if (payload.state === 'Inactive') {
				this.detachScreenshareVideo(payload.userId, true)
			}
		})
	}

	private attachCommandListeners() {
		if (!this.client) {
			throw new Error('Client not initialized in attachCommandListeners')
		}
		this._on(`command-channel-message`, (payload) => {
			if (!this.client) {
				this.consoleLog('Client not initialized in attachCommandListeners callback')
				return
			}
			if(!payload || !payload.text){
				this.consoleLog('command-channel-message - no text')
				return
			}
			try{
				const payloadData = JSON.parse(payload.text)
				this.handleCommand(payloadData)
				this.fire(MeetingEvent.CommandReceived, payloadData)
			}catch(e){
				this.consoleError('error parsing command - ' + payload.text, e)
			}
		});
	}



	private detachScreenshareVideo(userId: number, isActive: boolean) {
		if (!this.stream) {
			this.consoleLog('Stream not initialized in detachScreenshareVideo')
			return
		}
		if (!this._screenShareElement.value) {
			this.consoleLog('No screen share element to detach')
			return
		}

		const user = this.client?.getUser(userId)
		if (!user) {
			this.consoleLog('No user found to stop screenshare')
			return
		}

		if (!isActive) {
			// this isn't an active forced stop, so we need to check if the user is sharing
			if (!user.sharerOn) {
				this.consoleLog('User is not sharing, no need to detach')
				return
			}
		}

		this.consoleLog('detaching screenshare video', user)

		if ('' + userId == this.getUserId()) {
			// i'm sharing - detach the element with no fanfare
			this._isScreenshareVideoElementAttached.value = false
			this.fire(MeetingEvent.ScreenshareRemoved, { userId: userId, element: this._screenShareElement.value })
			this._screenShareElement.value = null
		} else {
			// someone else is sharing - stop the share render first, then detach the element
			if (isActive) {
				this.stream.stopShareView().then(async () => {
					this._isScreenshareVideoElementAttached.value = false
					this.fire(MeetingEvent.ScreenshareRemoved, { userId: userId, element: this._screenShareElement.value })
					this._screenShareElement.value = null

					// bug in canvas mode where the layout doesn't reset properly
					// so we need to detach the video, and then re-attach it
					if(this.iAmInCanvasMode){
						if(this.stream?.isCapturingVideo()){
							this.detachUserVideo(this.getUserIdNumber(), () => {
								this.attachUserVideo(this.getUserIdNumber())
							})
						}else{
							// synchronous
							this.detachUserPlaceholder(this.getUserIdNumber())
							this.attachUserPlaceholder(this.getUserIdNumber())
						}
					}
				}).catch((error) => {
					this.consoleLog('error stopping screen share', error)
				})
			}
		}
	}

	private _screenShareElement = ref(null as HTMLElement | null)
	private _videoObservers = new Map<number, MutationObserver>()
	public get screenShareElement(): HTMLElement | null {
		return this._screenShareElement.value
	}
	private attachScreenshareVideo(e: HTMLElement, userId: number) {
		if (!this.stream) {
			this.consoleLog('Stream not initialized in attachScreenshareVideo')
			return
		}

		this.stream.startShareView(e as HTMLCanvasElement, userId)
			.then(() => {
				this._isScreenshareVideoElementAttached.value = true
				this._screenShareElement.value = e
				this.fire(MeetingEvent.ScreenshareAdded, { userId: '' + userId, element: e } as ElementAddedEventArgs)
			})
			.catch((error) => {
				this.consoleLog('error starting screen share', error)
			})
	}

	public set mirrorVideo(value: boolean) {
		if (!this.stream) {
			this.consoleLog('Stream not initialized in mirrorVideo setter')
			return
		}
		this.stream.mirrorVideo(value).catch((error) => {
			this.consoleLog('error setting mirror video', error)
		})
	}

	public get iAmInCanvasMode(): boolean {
		return this._participants.value.some((p) => p.isOnCanvasMode && p.userId == this.getUserId())
	}


	private attachVideoObserver(element: HTMLElement, userId: number) {
		try {
			const observer = new MutationObserver((mutations) => {
				try {
					for (const mutation of mutations) {
						if (mutation.type === 'childList') {
							const video = element.querySelector('video')
							const hasVideo = video !== null;
							
							this._participants.value.forEach((p) => {
								if (p.userId == '' + userId) {
									p.isOnCanvasMode = !hasVideo
								}
							})
							
							if(!hasVideo && userId == this.getUserIdNumber()){
								// this is a canvas - apply zoom mirroring
								this.setMirroring()
							}

							// if this is a video element, and it hasn't loaded yet,
							// we add this extra hack to prevent the pause icon from showing
							// in iOS/Safari under low power mode; the autoplay attribute
							// is what causes the problem, but we want to be extra careful
							// that we only attach this if the video is not already loaded
							// to avoid issues of timing
							if(hasVideo && video.readyState === HTMLMediaElement.HAVE_NOTHING){
								video.removeAttribute('autoplay')
								video.addEventListener('loadedmetadata', () => {
									this.consoleLog('video loaded metadata', userId)
									video.play().catch((e) => {
										this.consoleError('error playing video in video observer', e)
									})
								})
								
							}
							
							const payload = {
								commandType: CommandType.SetParticipantOnCanvasMode,
								id: uuidv4(),
								senderId: this.getUserId(),
								timestamp: new Date().getTime(),
								isOnCanvasMode: !hasVideo
							}
							this.sendCommand(payload).catch((e) => {
								this.consoleError('Error sending set participant on canvas mode', e)
							})
						}
					}
				} catch (error) {
					console.error('Error in video observer callback:', error);
				}
			});
			
			observer.observe(element, { childList: true, subtree: true });
			
			// Store observer reference for cleanup
			this._videoObservers.set(userId, observer);
		} catch (error) {
			console.error('Error attaching video observer:', error);
		}
	}

	private detachVideoObserver(userId: number) {
		try {
			const observer = this._videoObservers.get(userId);
			if (observer) {
				observer.disconnect();
				this._videoObservers.delete(userId);
			}
		} catch (error) {
			console.error('Error detaching video observer:', error);
		}
	}

	private attachUserPlaceholder(userId: number) {
		if (!this.stream) {
			this.consoleLog('Stream not initialized in attachUserPlaceholder')
			return
		}
		if (!this.client?.getAllUser().some((user) => user.userId == userId)) {
			// user is not in the meeting
			this.consoleLog('user not in meeting - not attaching placeholder', userId)
			return
		}

		this.consoleLog('attaching user placeholder', userId)

		const container = document.createElement('div')
		container.setAttribute('data-user-id', '' + userId)
		//container.setAttribute('node-id', '' + userId)
		container.setAttribute('data-type', 'avatar')
		container.setAttribute('class', 'avatar')

		this._participants.value.forEach((p) => {
			if (p.userId == '' + userId) {
				p.element = container
			}
		})

		this.fire(MeetingEvent.PlaceholderAdded, { userId: '' + userId, element: container } as ElementAddedEventArgs)

		this.possiblySyncSelfCameraState(userId)

		return container
	}

	private attachUserVideo(userId: number) {
		this.consoleLog(`attaching user id ${userId}, is self: ${'' + userId == this.getUserId()}`)
		if (!this.stream) {
			this.consoleLog('Stream not initialized in attachUserVideo')
			return
		}
		if(!this.client) {
			this.consoleLog('Client not initialized in attachUserVideo')
			return
		}
		if (!this.client.getAllUser().some((user) => user.userId == userId)) {
			// user is not in the meeting
			this.consoleLog('user not in meeting - not attaching video')
			return
		}
		// attach hosts as low-quality video, everyone else as high quality
		// since the host is in the gallery, and the visitor is in the spotlight
		const isUserAHostOrManager = this.isUserAHostOrManager('' + userId)
		this.stream.attachVideo(userId, isUserAHostOrManager ? VideoQuality.Video_360P : VideoQuality.Video_720P).then((userVideo) => {
			if (!('length' in userVideo)) {
				// ensure the node id is set so this works
				const uv = (userVideo as VideoPlayer);
				//uv.setAttribute('node-id', '' + userId);
				uv.classList.add('video-player')
				// this ensures that safari doesn't show the video controls
				this._participants.value.forEach((p) => {
					if (p.userId == '' + userId) {
						p.element = uv
					}
				})
				// set self as a class on the element if this is us
				if ('' + userId == this.getUserId()) {
					uv.classList.add('self')
				} else {
					uv.classList.remove('self')
				}
				this.fire(MeetingEvent.VideoAdded, {
					userId: '' + userId,
					element: uv
				} as ElementAddedEventArgs)

				this.attachVideoObserver(uv, userId);

				this.possiblySyncSelfCameraState(userId)
			}
		}).catch((error) => {
			if(error && error.errorCode === 6001){
				this.consoleLog('error attaching user video - 6001, user is not sending video, we can ignore this')
			}else{
				this.consoleError('error attaching user video', error)
			}
		})
	}

	private possiblySyncSelfCameraState(userId: number) {
		// we're adding a video element, so match to the muted state
		// if it's us
		if(userId == this.getUserIdNumber() && this.stream){
			this._isCameraMuted.value = !this.stream.isCapturingVideo()
		}
	}

	public isFirstCamera() {
		if (this.isMobileDevice) {
			return this.activeCamera == 'user'
		}
		return this.cameras.findIndex((x) => x.deviceId == this.activeCamera) === 0
	}

	public isSpotlightUser(userId: number): boolean {
		if (!this.client) {
			throw new Error('Client not initialized in isSpotlightUser')
		}
		if (!this.stream) {
			return false
		}
		const spotLighted = this.stream.getSpotlightedUserList();
		if (spotLighted && spotLighted.length > 0 && spotLighted.some(x => x.userId == userId)) {
			return true
		}
		return false;
	}

	public async setSpotlight(userId: string): Promise<void> {
		if (!this.client) {
			throw new Error('Client not initialized in setSpotlight')
		}
		if (!this.stream) {
			return
		}
		try {
			if (this.isSpotlightUser(parseInt(userId, 10))) {
				// user is already spotlighted
				return
			}
			await this.stream.spotlightVideo(parseInt(userId, 10), true)
		} catch (e) {
			this.consoleLog('error setting spotlight', e)
		}
	}

	public async removeSpotlight(userId: string): Promise<void> {
		if (!this.client) {
			throw new Error('Client not initialized in removeSpotlight')
		}
		if (!this.stream) {
			throw new Error('Stream not initialized in removeSpotlight')
		}
		await this.stream.removeSpotlightedVideo(parseInt(userId, 10))
	}

	public detachUserPlaceholder(userId: number) {
		if (!this.stream) {
			this.consoleLog('Stream not initialized in detachUserPlaceholder')
			return
		}
		/*document.querySelectorAll(`div.avatar[user-id="${userId}"]`).forEach((element) => {
			this.fire(MeetingEvent.PlaceholderRemoved, { userId: userId, element })
		})*/
		this._participants.value.forEach((p) => {
			if (p.userId == '' + userId) {
				p.element = null
			}
		})
	}


	public detachUserVideo(userId: number, callback: () => void = () => {}) {
		if (!this.stream) {
			this.consoleLog('Stream not initialized in detachUserVideo')
			return
		}
		
		// Clean up video observer
		this.detachVideoObserver(userId);
		
		this.stream.detachVideo(userId).then((userVideo) => {
			if (!userVideo) {
				return
			}
			let videos = userVideo as VideoPlayer[]
			if (!('length' in userVideo)) {
				videos = [userVideo]
			}
			for (let video of videos) {
				this.fire(MeetingEvent.VideoRemoved, { userId: '' + userId, element: video as HTMLElement } as ElementRemovedEventArgs)
			}
			callback()
		}).catch((error) => {
			// in general, if we have an error DETACHING a video, it's not a big deal; ATTACHING is the real issue
			// it's expected to happen when the user leaves the meeting, or if the detach happens after a partial connection from a remote user,
			// so we don't want to log it as an error
			this.consoleLog('error detaching user video', error)
		});
	}


}
export { type ParticipantElement, type MediaDevice }
export { ZoomMeeting, MeetingEvent }

(window as any).ZoomMeeting = ZoomMeeting